/**
 * Web-app bridge to @cubby/shared — the only place that may import `@cubby/shared/src/...`.
 *
 * Import rules (feature code under `frontend/web-app/src`):
 * - Domain constants, workflow engines, roles, report parsers, shared types
 *   → import from `@core/shared-bridge` (add missing exports here; do not deep-import shared).
 * - Map / object-list display helpers (status text, phase labels, address formatting, filter ordering)
 *   → import from `@shared/utils/object.utils` (re-exports selected bridge symbols for UI).
 * - Never import the `@cubby/shared` package barrel (`from '@cubby/shared'`) in web code.
 *
 * See `docs/frontend/import-conventions.md`.
 */

// --- Interfaces ---
export {
  STATUS_RED,
  STATUS_YELLOW,
  STATUS_GREEN,
  OBJECT_TYPES,
  CZECH_STATUS,
  type GeoLocation,
  type ObjectMetadata,
  type ObjectType,
} from '@cubby/shared/src/interfaces/object.interface';

export type {
  PolygonStatus,
} from '@cubby/shared/src/interfaces/polygon.interface';

export type {
  TenantAppSettingsDto,
  TenantOrgConfigDto,
  TenantConnectionDto,
  TenantConnectionKind,
  TenantCompanyDto,
  TenantBranchDto,
  TenantMailingGroupDto,
  UpsertTenantCompanyInput,
  UpsertTenantBranchInput,
  UpsertTenantMailingGroupInput,
  TenantOrgSignatory,
} from '@cubby/shared/src/interfaces/org-config.interface';

export {
  orgEntityDisplayParts,
  isOrgSecondaryCodeRedundant,
} from '@cubby/shared/src/utils/org-config.util';
export {
  resolveTenantCompanyId,
  type TenantCompanyAssignmentHints,
  type TenantCompanyMatchRef,
} from '@cubby/shared/src/utils/tenant-company-match.util';

// --- Status & phase colors ---
export {
  STATUS_COLORS,
  getStatusColor,
  getObjectStatusColor,
} from '@cubby/shared/src/constants/status.constants';

export {
  PHASE_NAME,
  PHASE_CODE,
  normalizeObjectTypeForLookup,
} from '@cubby/shared/src/constants/object-workflow/phase-codes';

/** Object workflow — canonical entry: `workflow-model.ts` (see object-workflow/README.md). */
export {
  PHASE_COLORS,
  getPhaseName,
  resolvePhaseLabel,
  getPhaseByCode,
  getPhaseInfoByCode,
  parseWorkflowVariant,
  getPreviousPhaseCodes,
  getNextPhaseCodes,
  getNextPhases,
  getPreviousPhases,
  getWorkflowForType,
  getAllPhasesForType,
  getObjectTypesWithWorkflow,
  getPhaseColor,
  getLineStyle,
  hasWorkflow,
  GENERATED_WORKFLOWS,
  getInitialPhase,
  isSamePhase,
  hasReachedWorkflowMilestone,
  isValidTransition,
  filterPreviousPhasesByObjectHistoryTimeline,
  WORKFLOW_METADATA_KEYS,
  resolveMapDisplayPhaseCode,
  type WorkflowPhase,
  type ObjectTypeWorkflow,
} from '@cubby/shared/src/constants/object-workflow.constants';

export { getObjectPhaseProgressPercent } from '@cubby/shared/src/constants/object-workflow/phase-progress';

export {
  CONSTRUCTION_PROGRESS_CATEGORY_IDS,
  CONSTRUCTION_PROGRESS_CATEGORY_LABELS,
  CONSTRUCTION_CATEGORY_OBJECT_TYPES,
  CONSTRUCTION_CATEGORY_FINANCE_WORK_BUCKETS,
  buildConstructionCategoryProgressRows,
  completionPercentForConstructionCategory,
  weightedOverallConstructionCompletion,
  type ConstructionProgressCategoryId,
  type ConstructionCategoryProgressRow,
  FINANCE_WORK_TABLE_TOP_CATEGORY_IDS,
  FINANCE_WORK_TABLE_TOP_CATEGORY_LABELS,
  FINANCE_WORK_TABLE_STAVBA_SUBCATEGORY_IDS,
  FINANCE_WORK_TABLE_STAVBA_SUBCATEGORY_LABELS,
  FINANCE_WORK_TABLE_MONTAZ_SUBCATEGORY_IDS,
  FINANCE_WORK_TABLE_MONTAZ_SUBCATEGORY_LABELS,
  FINANCE_WORK_TABLE_CATEGORY_IDS,
  FINANCE_WORK_TABLE_CATEGORY_LABELS,
  FINANCE_WORK_TABLE_BUCKET_MAP,
  resolveFinanceWorkTableCategoryFromLine,
  resolveFinanceWorkTableStavbaSubcategoryFromLine,
  resolveFinanceWorkTableMontazSubcategoryFromLine,
  type FinanceWorkTableTopCategoryId,
  type FinanceWorkTableStavbaSubcategoryId,
  type FinanceWorkTableMontazSubcategoryId,
  type FinanceWorkTableCategoryId,
} from '@cubby/shared/src/constants/construction-progress-categories.constants';

export {
  MANAGER_KPI_CATEGORY_IDS,
  MANAGER_KPI_CATEGORY_LABELS,
  MANAGER_KPI_CATEGORY_SUMMARIES,
  MANAGER_KPI_CATEGORY_HEADLINE,
  MANAGER_KPI_CARD_IDS,
  MANAGER_KPI_CARD_LABELS,
  MANAGER_KPI_CARD_INFO,
  MANAGER_KPI_HEADLINE_INFO,
  MANAGER_KPI_CATEGORY_INFO,
  MANAGER_KPI_CATEGORY_CARDS,
  MANAGER_KPI_CATEGORY_DETAIL_EXTRA_CARDS,
  MANAGER_KPI_BUILDING_DUAL_CARD_IDS,
  MANAGER_KPI_ABSOLUTE_CARD_IDS,
  MANAGER_KPI_CARD_FRONTA_TAB,
  managerKpiCategoryDetailCards,
  frontaTabForManagerKpiCard,
  isManagerKpiBuildingDualCardId,
  isManagerKpiAbsoluteCardId,
  isManagerKpiCardFrontaDrillable,
  isManagerKpiCategoryId,
  isManagerKpiCategoryDrillable,
  type ManagerKpiCategoryId,
  type ManagerKpiCardId,
  type ManagerKpiCategoryHeadlineId,
} from '@cubby/shared/src/constants/manager-kpi-categories.constants';

export {
  resolveWorkReportFinanceBucket,
  resolvePpdMaterialFinanceBucket,
  resolvePpdWorkFinanceBucket,
  isMontazMultiductWorkName,
  isFinanceStavbaPokladkaLine,
  isFinanceStavbaMultiductLine,
  isFinanceStavbaVykopLine,
  isFinanceStavbaObnovaLine,
  isFinanceStavbaOstatniLine,
  isZafukWorkName,
  FINANCE_MONTAZ_MULTIDUCT_SAP,
  FINANCE_WORK_BUCKET_LABELS,
  FINANCE_MATERIAL_BUCKET_LABELS,
} from '@cubby/shared/src/constants/ppd-finance-categories.constants';

export {
  BUDOVA_BUILDING_TYPES,
  BUDOVA_BUILDING_TYPE_LABELS,
  isBudovaBuildingType,
  AKVIZICE_DRAWING_SIGN_BUILDING_TYPE,
  AKVIZICE_DRAWING_REJECT_BUILDING_TYPE,
  AKVIZICE_DRAWING_BUILDING_TYPE_LOCKED_MESSAGE,
  type BudovaBuildingType,
} from '@cubby/shared/src/constants/object-workflow/budova-building-type.constants';

export {
  BABOVKA_PLACEMENTS,
  BABOVKA_PLACEMENT_LABELS,
  isBabovkaPlacement,
  formatBabovkaPlacementLabel,
  readBabovkaPlacementFromMetadata,
  type BabovkaPlacement,
} from '@cubby/shared/src/constants/object-workflow/babovka-placement.constants';

export {
  BABOVKA_REQUIRED_MATERIAL_SAP_CODES,
  BABOVKA_REQUIRED_MATERIAL_LABELS,
  BABOVKA_MATERIAL_RD_EXPORT_LABELS,
  BABOVKA_MATERIAL_REQUIRED_MESSAGE,
  isBabovkaRequiredMaterialSap,
  hasRequiredBabovkaMaterial,
  isBudovaBabovkaMaterialReady,
  resolveBabovkaRequiredMaterialSap,
  formatBabovkaMaterialRdExportLabel,
  type BabovkaRequiredMaterialSapCode,
} from '@cubby/shared/src/constants/object-workflow/babovka-material.constants';

export {
  isZafukMilestoneSpecValid,
  isZafukPhaseTransitionValid,
  isZafukRozvadecLockedFromFk,
  recomputeZafukLengthFromOkMetraz,
  parseZafukMetaDouble,
} from '@cubby/shared/src/constants/object-workflow/zafuk-spec.constants';

export {
  ZAFUK_SOURCE_OBJECT_TYPES,
  ZAFUK_SOURCE_TYPE_LABELS,
  ZAFUK_SOURCE_PICK_FROM_MAP_LABEL,
  ZAFUK_SOURCE_PICK_FROM_LIST_LABEL,
  ZAFUK_SOURCE_CLOSE_LIST_LABEL,
  ZAFUK_SOURCE_SEARCH_PLACEHOLDER,
  ZAFUK_SOURCE_PICK_CONFIRM_LABEL,
  ZAFUK_SOURCE_PICK_CANCEL_LABEL,
  ZAFUK_SOURCE_PICK_HINT,
  ZAFUK_SOURCE_LABEL,
  ZAFUK_SOURCE_EMPTY_LABEL,
  ZAFUK_SOURCE_REQUIRED_MESSAGE,
  ZAFUK_SOURCE_TYPE_INVALID_MESSAGE,
  ZAFUK_SOURCE_SELF_MESSAGE,
  ZAFUK_SOURCE_POLYGON_MESSAGE,
  ZAFUK_SOURCE_NOT_FOUND_MESSAGE,
  ZAFUK_SOURCE_LIST_NO_POLYGON_MESSAGE,
  ZAFUK_SOURCE_LIST_LOAD_ERROR,
  ZAFUK_SOURCE_LIST_EMPTY_MESSAGE,
  ZAFUK_SOURCE_LIST_NO_MATCH_MESSAGE,
  ZAFUK_SOURCE_REJECTION_MESSAGES,
  isZafukSourceObjectType,
  parseZafukSourceObjectType,
  zafukSourceLabel,
  createZafukSourceRef,
  zafukSourceRefsEqual,
  normalizeZafukSourceRef,
  zafukSourceToFlatFields,
  zafukSourceToLegacyFields,
  zafukSourceWritesLegacyRozvadecFields,
  evaluateZafukSourceCandidate,
  isZafukSourceCandidateEligible,
  describeZafukSourceCandidateFailure,
  zafukSourcePickableObjectTypes,
  isZafukSourcePickableObjectType,
  type ZafukSourceObjectType,
  type ZafukSourceRef,
  type ZafukSourceFlatFields,
  type ZafukSourceLegacyFields,
  type ZafukSourceTargetContext,
  type ZafukSourceCandidate,
  type ZafukSourceRejectionReason,
  type ZafukSourceEligibility,
} from '@cubby/shared/src/constants/object-workflow/zafuk-source.constants';

export {
  ROZVADEC_ZAFUK_KINDS,
  ROZVADEC_ZAFUK_PICKER_KINDS,
  ROZVADEC_ZAFUK_KIND_LABELS,
  ROZVADEC_ZAFUK_KIND_UNSET_LABEL,
  ROZVADEC_ZAFUK_KIND_REQUIRED_MESSAGE,
  type RozvadecZafukKind,
  type RozvadecZafukEntry,
  createEmptyRozvadecZafukEntry,
  isRozvadecZafukKind,
  isRozvadecZafukMicrotubeKind,
  rozvadecZafukKindLabel,
  objectTypeUsesRozvadecZafukEntries,
  normalizeRozvadecZafukEntries,
  areRozvadecZafukEntriesValid,
  applyTypOkFromMaterialsToZafukEntries,
  deriveBudovaZafukTypOkFromMaterials,
  rozvadecZafukNeedsOpticalCableMaterial,
  rozvadecZafukEntryLengthM,
  sumRozvadecZafukEntriesLengthM,
  resolveRozvadecZafukLengthM,
  serializeRozvadecZafukEntriesForCommit,
  serializeKoncovyBodZafukEntriesForCommit,
  describeRozvadecZafukEntriesValidationFailure,
  areKoncovyBodZafukEntriesValid,
  describeKoncovyBodZafukEntriesValidationFailure,
  KONCOVY_BOD_ZAFUKU_VYCHOZI_BOD_FORBIDDEN_MESSAGE,
  stripKoncovyBodZafukVychoziBod,
  outgoingWeldCableId,
  isOutgoingWeldCableId,
  peerLookupCablesPointingAtSource,
  buildRozvadecZafukInfoReviewLines,
  buildRozvadecZafukInfoBlocks,
  type RozvadecZafukInfoBlock,
  type RozvadecZafukInfoRow,
  sumRozvadecZafukWorkReportMeters,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-zafuk.constants';

export {
  SPOJKOVISTE_BOD_ZAFUKU_METADATA_KEY,
  SPOJKOVISTE_BOD_ZAFUKU_KINDS,
  SPOJKOVISTE_BOD_ZAFUKU_KIND_LABELS,
  SPOJKOVISTE_BOD_ZAFUKU_KIND_UNSET_LABEL,
  SPOJKOVISTE_BOD_ZAFUKU_KIND_REQUIRED_MESSAGE,
  SPOJKOVISTE_BOD_ZAFUKU_METRAZ_REQUIRED_MESSAGE,
  SPOJKOVISTE_BOD_ZAFUKU_TYP_OK_REQUIRED_MESSAGE,
  SPOJKOVISTE_BOD_ZAFUKU_CHECKBOX_LABEL,
  SPOJKOVISTE_BOD_ZAFUKU_ROZVADEC_LABEL,
  SPOJKOVISTE_BOD_ZAFUKU_ROZVADEC_REQUIRED_MESSAGE,
  emptySpojkovisteBodZafukuSpec,
  spojkovisteBodZafukuKindLabel,
  spojkovisteBodZafukuLengthM,
  applyTypOkFromMaterialsToSpojkovisteBodZafuku,
  normalizeSpojkovisteBodZafuku,
  normalizeSpojkovisteBodZafukuFromZafukColumns,
  serializeSpojkovisteBodZafukuForCommit,
  serializeSpojkovisteBodZafukuForBaseline,
  spojkovisteBodZafukuNeedsOpticalCableMaterial,
  spojkovisteBodZafukuRequiresSourceRozvadec,
  withInheritedSpojkovisteBodZafukuSourceRozvadec,
  areSpojkovisteBodZafukuFieldsValid,
  areSpojkovisteBodZafukuValid,
  describeSpojkovisteBodZafukuFieldsFailure,
  describeSpojkovisteBodZafukuValidationFailure,
  buildSpojkovisteBodZafukuInfoRows,
  type SpojkovisteBodZafukuKind,
  type SpojkovisteBodZafukuSpec,
  type SpojkovisteBodZafukuInfoRow,
  type SpojkovisteBodZafukuFieldOptions,
} from '@cubby/shared/src/constants/object-workflow/spojkoviste-bod-zafuku.constants';

export {
  ROZVADEC_CONNECTION_KINDS,
  ROZVADEC_CABINET_KINDS,
  ROZVADEC_CABINET_TYPE_IDS,
  ROZVADEC_SPLITTER_RATIOS,
  ROZVADEC_MATERIAL_IDENTITIES,
  ROZVADEC_CABINET_TYPES,
  getRozvadecCabinetType,
  getRozvadecMaterialIdentity,
  resolveRozvadecCabinetTypeByAlias,
  resolveRozvadecCabinetTypeFromMaterials,
  hasRequiredRozvadecCabinetMaterial,
  findRozvadecCabinetMaterialConflict,
  collectRozvadecCabinetMaterialMatches,
  isRozvadecConnectionKindAllowed,
  resolveRozvadecMaxWeldCountFromMaterials,
  resolveRozvadecWeldLimitFromMaterials,
  isRozvadecWeldCountWithinKnownLimit,
  secondaryWeldCountForCabinetType,
  cabinetGridForType,
  cabinetGridCellsForType,
  cabinetSectionSlotCount,
  cabinetCassetteRackForType,
  cabinetCassetteRackSection,
  splitterRatioOutputCount,
  isRozvadecCabinetTypeId,
  isRozvadecConnectionKind,
  type RozvadecConnectionKind,
  type RozvadecCabinetKind,
  type RozvadecCabinetTypeId,
  type RozvadecSplitterRatio,
  type RozvadecCabinetSection,
  type RozvadecCabinetTypeDef,
  type RozvadecCabinetCassetteRack,
  type RozvadecCabinetCassetteSection,
  type RozvadecCassetteFillDirection,
  type RozvadecMaterialIdentity,
  type RozvadecMaterialUsageLike,
  type RozvadecCabinetTypeResolveResult,
  type RozvadecCabinetGrid,
  type RozvadecCabinetGridCell,
  type RozvadecCabinetMaterialMatch,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-cabinet.constants';

export {
  ROZVADEC_WELD_UNITS,
  ROZVADEC_CAPACITY_MODES,
  ROZVADEC_WELD_CAPACITY_BY_SAP,
  ROZVADEC_ACCESSORY_CAPACITY_BY_SAP,
  resolveRozvadecLiveWeldCapacity,
  getRozvadecWeldCapacityProfile,
  derivedIdentityMaxWeldCount,
  growingWeldSeatCount,
  type RozvadecWeldUnit,
  type RozvadecCapacityMode,
  type RozvadecWeldCapacityProfile,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-weld-capacity';

export {
  RAY_TR3_RACK_HEIGHT_U,
  RAY_OFSM_COLUMN_SIZE,
  RAY_OFSM_MODULES,
  getRayOfsmModule,
  hasRozvadecOfsmRack,
  rayOfsmTraysFor,
  rayOfsmConnectorCoord,
  rayOfsmConnectorPositions,
  rayOfsmColumnCount,
  rayOfsmOccupiedUs,
  type RayOfsmModuleDef,
  type RayOfsmSap,
  type RayOfsmKind,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-ofsm.constants';

export {
  emptyRsuOccupancy,
  assignRsuOdfOrder,
  findFirstFreeRsuSpan,
  canPlaceRsuModule,
  placeRsuModule,
  moveRsuModule,
  removeRsuModule,
  firstFreeRsuConnector,
  assignRsuFiberRange,
  addRsuPatchConnection,
  tryAddRsuPatchConnection,
  removeRsuPatchConnection,
  removeRsuTermination,
  findRsuPatchForPort,
  rsuPatchPortKey,
  formatRsuD5Address,
  rsuStatusLabelCs,
  readRsuOccupancy,
  rsuOfsmMaterialShortfall,
  validateRsuOccupancy,
  type RsuOccupancy,
  type RsuOfsmModule,
  type RsuTermination,
  type RsuWeldConnection,
  type RsuPatchConnection,
  type RsuSplitterPath,
  type RsuTerminationStatus,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-rsu-occupancy';

export {
  ROZVADEC_CASSETTE_TYPE_IDS,
  ROZVADEC_CASSETTE_TYPES,
  isRozvadecCassetteTypeId,
  getRozvadecCassetteType,
  isRozvadecWeldTrayCassette,
  rozvadecWeldTraySeatCount,
  isRozvadecWeldTraySeat,
  rozvadecWeldTraySeatLabel,
  rozvadecCassetteOccupiedPositions,
  rozvadecCassetteDisplayPosition,
  formatRozvadecKm5ConnectorLabel,
  formatRozvadecKm5PositionLabel,
  type RozvadecCassetteTypeId,
  type RozvadecCassetteTypeDef,
  type RozvadecCassetteSpan,
  type RozvadecCassetteContentKind,
  type RozvadecCassettePreinstalledSplitters,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-cassette.constants';

export {
  ROZVADEC_MUK_RACK_ROWS,
  rozvadecMukRackCellPosition,
  rozvadecMukRackCellFromPosition,
  rozvadecMukRackDisplayNumber,
  rozvadecMukRackNumberingCells,
  rozvadecMukRackOccupiedDisplayNumbers,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-muk-rack';

export {
  deriveRozvadecWeldCounts,
  uniqueIncomingCableFiberKeys,
  countAssignedBranches,
  countUnassignedDirects,
  isCompleteCableFiberPair,
  rozvadecFiberHopKey,
  type RozvadecWeldCountRow,
  type RozvadecDerivedWeldCounts,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-weld-count';

export {
  calculateCabinetWeldCount,
  calculateWeldCount,
  formatInstallationPricePreview,
  INSTALLATION_PRICE_RULE_MISSING_CS,
  ROZVADEC_PROVARENE_REQUIRES_WELD_CS,
  resolveCabinetInstallationPrice,
  resolveInstallationIdentitySap,
  resolveInstallationPrice,
  weldBillingLayoutFromCabinet,
  type WeldBillingMetric,
  type WeldBillingSource,
  type WeldLayout,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-weld-billing';

export {
  ROZVADEC_WELD_LAYOUT_VERSION,
  ROZVADEC_WELD_CASSETTE_POSITION_BASE,
  ROZVADEC_WELD_CASSETTE_SLOT_BASE,
  ROZVADEC_WELD_LAYOUT_SPLITTER_OUTPUT_BASE,
  ROZVADEC_WELD_TERMINATION_KINDS,
  ROZVADEC_WELD_LINK_KINDS,
  ROZVADEC_WELD_LAYOUT_ISSUE_CODES,
  isRozvadecWeldLayoutVersion,
  isRozvadecWeldTerminationKind,
  isRozvadecWeldLinkKind,
  isRozvadecWeldLayoutIssueCode,
  isRozvadecWeldLayoutCompletenessIssue,
  ROZVADEC_WELD_LAYOUT_COMPLETENESS_ISSUE_CODES,
  defaultRozvadecWeldLayoutOffers,
  emptyRozvadecWeldLayout,
  enforceRozvadecWeldLayoutOffers,
  disconnectNonOsCassetteBypassDirects,
  isOsDirectOnlyWeldType,
  rozvadecAllowsNewDirectSplices,
  hasRozvadecCassetteRack,
  resolveWeldFiberCapacity,
  expandWeldCableFibers,
  inspectRozvadecOriginWeldCablesFromZafuk,
  outgoingCablesFromPeerLookups,
  outgoingCablesFromLocalOrigin,
  resolveOutgoingWeldCables,
  assignWeldCablesToRails,
  isBudovaWeldCableId,
  weldCableDestRole,
  type WeldCableFiberRole,
  type WeldRailAssignedCable,
  type WeldRailAssignment,
  type WeldVisualDestRole,
  findWeldLayoutCable,
  findWeldLayoutBuilding,
  emptyRozvadecWeldCassetteCounts,
  rozvadecWeldTrayCapacity,
  rozvadecCassetteTerminationSlotCount,
  deriveRozvadecWeldLayoutCounts,
  deriveRozvadecWeldLayoutOccupancy,
  findPendingCassettePair,
  rozvadecWeldTraySeatRows,
  weldLinkSeat,
  incomingRefsEqual,
  isRozvadecBuildingDirectLink,
  isRozvadecBuildingTargetedLink,
  isRozvadecCassetteBoundDirect,
  isRozvadecCassetteBypassDirect,
  isRozvadecSplitterReservedLink,
  linksForCassette,
  rozvadecCassettePairCount,
  rozvadecCassetteWeldUsedCount,
  resolveRozvadecWeldLayoutCapacity,
  incomingKeysForCable,
  isPersistedWeldLayoutId,
  rozvadecWeldIncomingKey,
  rozvadecWeldIncomingRefKey,
  rozvadecWeldTargetKey,
  rozvadecWeldSplitterOutputKey,
  rozvadecWeldRackPositionKey,
  rozvadecWeldCassetteSlotKey,
  hasWeldCassetteSlot,
  isCompleteWeldIncomingRef,
  isUnpairedWeldSplitter,
  isCompleteWeldTargetRef,
  rozvadecWeldLinkSourceKey,
  rozvadecWeldCassetteSignature,
  rozvadecWeldTerminationSignature,
  rozvadecWeldLinkSignature,
  rozvadecWeldLayoutPersistSignature,
  weldLayoutsPersistEquivalent,
  deterministicWeldCassetteId,
  deterministicWeldTerminationId,
  deterministicWeldLinkId,
  resolveWeldCassetteId,
  resolveWeldTerminationId,
  resolveWeldLinkId,
  compareWeldCassettes,
  compareWeldTerminations,
  compareWeldLinks,
  sortWeldLayout,
  normalizeRozvadecWeldLayout,
  normalizeRozvadecWeldLayoutValue,
  canonicalizeRozvadecWeldLayout,
  readRozvadecWeldLayoutOffers,
  validateRozvadecWeldLayout,
  validateRozvadecWeldLayoutInput,
  uniqueSortedIssueCodes,
  evaluateRozvadecWeldLayoutCompletion,
  type RozvadecWeldLayoutVersion,
  type RozvadecWeldTerminationKind,
  type RozvadecWeldLinkKind,
  type RozvadecWeldLayoutIssueCode,
  type RozvadecWeldLayoutOffers,
  type RozvadecWeldIncomingRef,
  type RozvadecWeldTargetRef,
  type RozvadecWeldCassette,
  type RozvadecWeldCassetteCountsByType,
  type RozvadecWeldTermination,
  type RozvadecWeldLink,
  type RozvadecWeldLayout,
  type RozvadecWeldLayoutCable,
  type RozvadecWeldLayoutBuilding,
  type RozvadecWeldLayoutContext,
  type RozvadecWeldLayoutIssue,
  type RozvadecWeldLayoutCounts,
  type RozvadecWeldFreeSplitterOutput,
  type RozvadecWeldFreeCassetteSlot,
  type RozvadecWeldLayoutOccupancy,
  type RozvadecWeldLayoutCapacity,
  type RozvadecWeldLayoutCompletion,
  type RozvadecWeldLayoutNormalizeResult,
  type RozvadecWeldTraySeatRow,
  occupancyEndpointId,
  parseOccupancyEndpointId,
  addOccupancyWeldConnection,
  isOccupancyFiberFiberPair,
  isOccupancyFiberSplitterPortPair,
  occupancyWeldFiberEnd,
  addOccupancyPatchConnection,
  removeOccupancyConnection,
  occupancyConnectionIdForEndpoint,
  validateCassetteOccupancyConnections,
  deriveOccupancyServicePaths,
  occupancyConnectionOppositeLabel,
  occupancyIssueMessageCs,
  occupancyEndpointHasWeld,
  occupancyEndpointHasPatch,
  occupancyFiberIsOutgoing,
  occupancySameConnectorChannel,
  resolveConnectorSide,
  adapterThroughPeerId,
  connectorChannelsForCassette,
  cassetteIdsForConnection,
  layoutWeldConnections,
  layoutPatchConnections,
  layoutSplitterPaths,
  type OccupancyEndpoint,
  type OccupancyConnectorChannel,
  type OccupancyConnectorSide,
  type OccupancyCableRole,
  type OccupancyConnectionIssue,
  type OccupancyWriteResult,
  type OccupancyServicePath,
  type OccupancyWeldConnection,
  type OccupancyPatchConnection,
  type OccupancySplitterPath,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-weld-layout';

export {
  generateRozvadecOccupancySchema,
  occupancyKm5ConnectorPrintLabel,
  type RozvadecOccupancySchema,
  type RozvadecOccupancyGenerateResult,
  type RozvadecOccupancyBlocker,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-occupancy-schema';

export {
  FIBER_COLOR_PROFILES,
  FIBER_CABLE_KINDS,
  FIBER_COLOR_IDS,
  FIBERS_PER_BUFFER,
  FIBER_STRIPE_FROM_POSITION,
  FIBER_BUFFER_COUNT,
  FIBER_NUMBER_MAX,
  FIBER_COLORS_PER_GROUP,
  FIBER_BUFFER_COLORS,
  FIBER_COLOR_LABELS_CS,
  FIBER_COLOR_HEX,
  FIBER_COLOR_PROFILE_LABELS_CS,
  FIBER_CABLE_KIND_LABELS_CS,
  FIBER_COLOR_CODE_ERRORS,
  isFiberColorProfileId,
  isFiberCableKind,
  isFiberColorId,
  fiberColorProfileForInvestor,
  fiberColorOrderFor,
  fiberNumberToColors,
  colorsToFiberNumber,
  fiberNumberToColorsOrNull,
  colorsToFiberNumberOrNull,
  type FiberColorProfileId,
  type FiberCableKind,
  type FiberColorId,
  type FiberBufferColorId,
  type FiberColorOrder12,
  type FiberColorAssignment,
  type FiberColorCodeError,
  type FiberColorCodeResult,
} from '@cubby/shared/src/constants/object-workflow/fiber-color-code.constants';

export {
  BUDOVA_TYP_OK_VALUES,
  ROZVADEC_TYP_OK_VALUES,
  TYP_OK_VALUES,
  ALL_TYP_OK_VALUES,
  isPolygonFis,
  isTypOkValue,
  isAnyTypOkValue,
  TYP_OK_FIBER_COUNT,
  fiberCountFromTypOk,
  isFiberNumberWithinApprovedTypOkCapacity,
  resolveTypOkByHp,
  type TypOkValue,
  type RozvadecTypOkValue,
} from '@cubby/shared/src/constants/object-workflow/typ-ok.constants';

export {
  OK_MATERIAL_REQUIRED_MESSAGE,
  OK_MATERIAL_SAP_TO_TYP_OK,
  hasRequiredOkCableMaterial,
  isBudovaZafukOkMaterialReady,
  typOkFromMaterial,
  typOkFromSapCode,
  resolveTypOkFromMaterials,
  type OkMaterialUsageLike,
} from '@cubby/shared/src/constants/object-workflow/typ-ok-from-material.constants';

export { isVsoHp, isMsoHp, VSO_HP_MIN, resolveBudovaHpCountForPopup, canSetDigitalniCestaCount, clampDigitalniCestaCount, maxDigitalniCestaCount, DIGITALNI_CESTA_EXCEEDS_HP_MESSAGE, DIGITALNI_CESTA_HP_HINT } from '@cubby/shared/src/utils/budova-hp-display.util';
export {
  PRESENCE_FILTER_OPTIONS,
  COUNT_FILTER_OP_OPTIONS,
  OBJECT_ATTR_FILTER_DIM,
  OBJECT_ATTR_FILTER_GROUP_LABELS,
  OBJECT_ATTR_FILTER_DIMENSIONS,
  OBJECT_ATTR_FILTER_HP_KIND_OPTIONS,
  OBJECT_ATTR_FILTER_MILESTONE_OPTIONS,
  OBJECT_ATTR_FILTER_BUILDING_TYPE_UNSET,
  OBJECT_ATTR_FILTER_BUILDING_TYPE_OPTIONS,
  emptyCountFilter,
  emptyObjectAttributeFilterState,
  simplifyObjectAttributeFilterState,
  cyclePresenceFilterValue,
  isCountFilterActive,
  normalizeCountFilter,
  matchesCountFilter,
  formatCountFilterBadge,
  parseCountFilterExpression,
  OBJECT_ATTR_FILTER_QUERY_KEYS,
  countFilterToQueryParam,
  parseCountFilterQueryParam,
  objectAttributeFilterStateFromQuery,
  objectAttributeFilterStateToQueryParams,
  expandObjectAttributeStatusFilterCodes,
  objectAttributeStatusFilterAppliesToType,
  isObjectAttributeFilterActive,
  countActiveObjectAttributeFilters,
  dimensionsForObjectTypes,
  objectMatchesAttributeFilter,
  extractObjectAttributeFilterFacts,
  budovaMissingDigitalniCestaAffordance,
  OBJECT_ATTR_FILTER_SIMPLE_DIMS,
  type PresenceFilterValue,
  type CountFilterOp,
  type CountFilterValue,
  type ObjectAttributeFilterState,
  type ObjectAttributeFilterFacts,
  type ObjectAttrFilterDimDef,
  type ObjectAttrFilterGroupId,
  type ObjectAttrFilterDimId,
} from '@cubby/shared/src/utils/object-attribute-filter.util';
export {
  BUDOVA_PRIPOJENI_CHECK_COLUMNS,
  BUDOVA_PRIPOJENI_EXCLUSION_FILL_ARGB,
  WF_HEADER_TO_PRIPOJENI_CHECK_COLUMN,
  isBudovaZrusenaPhase,
  isBudovaPripojeniColumnExcluded,
  resolveBudovaPripojeniExclusions,
  resolveManualHpForPripojeniExclusion,
  resolveWfHeaderExclusionColumn,
  type BudovaPripojeniCheckColumn,
  type BudovaPripojeniExclusions,
  type BudovaPripojeniExclusionInput,
} from '@cubby/shared/src/utils/budova-pripojeni-exclusion.util';

export {
  buildBudovaPripojeniMilestoneEngineContext,
  countBudovaPripojeniOdbocenoComplete,
  isBudovaPripojeniOdbocenoComplete,
  type BudovaPripojeniListBranchFields,
} from '@cubby/shared/src/utils/budova-pripojeni-milestone-context.util';

export {
  BUDOVA_PRIPOJENI_CHECK_COLUMN_LABELS,
  BUDOVA_PRIPOJENI_TABLE_EXPORT_HEADERS,
  buildBudovaPripojeniTableExportAoA,
  formatBudovaPripojeniCheckExportCell,
  resolveBudovaPripojeniContact,
  type BudovaPripojeniContactSources,
  type BudovaPripojeniTableExportAoA,
  type BudovaPripojeniTableExportRow,
} from '@cubby/shared/src/utils/budova-pripojeni-table-export.util';

export {
  RD_MISSING_DATA_FILL_ARGB,
  RD_TABLE_EXPORT_HEADERS,
  BABOVKA_PLACEMENT_RD_LAND_LABELS,
  buildRdExportRow,
  formatBabovkaKrytOkRdExportLabel,
  formatBabovkaPlacementRdLandLabel,
  formatDigitalniCestaUmistenniRdExportLabel,
  normalizeRdExportHeader,
  resolveRdAddressFieldsFromBudova,
  resolveRdExportCellValue,
  resolveRdExportHeaderKey,
  resolveRdExportMissingFlags,
  resolveRdExportRedColumnIndexes,
  resolveRdExportRedHeaderKeys,
  type RdExportCellValue,
  type RdExportHeaderKey,
  type RdExportMissingFlags,
  type RdExportObjectInput,
} from '@cubby/shared/src/utils/rd-table-export.util';
export {
  aggregateBudovaPripojeniNotes,
  buildBudovaPripojeniNotesLookup,
  formatBudovaPripojeniAggregatedNoteLine,
  formatBudovaPripojeniNotesDisplay,
  parseWorkflowObjectNotesFromMetadata,
  resolveBudovaPripojeniClusterObjectIds,
  BUDOVA_PRIPOJENI_NOTE_SOURCE_LABELS,
  type BudovaPripojeniAggregatedNote,
  type BudovaPripojeniClusterInput,
  type BudovaPripojeniNoteSource,
  type BudovaPripojeniNotesLookup,
  type BudovaPripojeniNotesLookupEntry,
  type WorkflowObjectNoteParsed,
} from '@cubby/shared/src/utils/budova-pripojeni-notes.util';

export {
  formatBudovaAddressLabel,
  formatBudovaCpLabel,
  formatBudovaDisplayName,
  resolveBudovaRdAddressFields,
  resolveBudovaTypCisla,
  toBudovaAcquisitionAddress,
  toBudovaAddressLabelInput,
  type BudovaAcquisitionAddress,
  type BudovaAddressLabelInput,
  type BudovaRdAddressFields,
} from '@cubby/shared/src/utils/budova-address-label.util';

export {
  formatHouseNumberLabel,
  normalizeTypCisla,
  stripHouseNumberPrefix,
  typCislaOrDefault,
  typCislaPrefix,
  TYP_CISLA_COLUMN_LABEL,
  TYP_CISLA_EVIDENCNI,
  TYP_CISLA_POPISNE,
  TYP_CISLA_PREFIX,
  type TypCisla,
} from '@cubby/shared/src/utils/typ-cisla.util';

export {
  UKONCENI_V_RD_VALUES,
  UKONCENI_V_RD_LABELS,
  isUkonceniVRd,
  ukonceniVRdRequiresOtherText,
  requiresUkonceniVRdForZafuk,
  requiresUkonceniVRdForZavrt,
  isUkonceniVRdSelectionValid,
  isZavrtMilestoneSpecValid,
  formatUkonceniVRdLabel,
  type UkonceniVRd,
} from '@cubby/shared/src/constants/object-workflow/ukonceni-v-rd.constants';

export {
  DIGITALNI_CESTA_SPEC_INCOMPLETE_MESSAGE,
  DIGITALNI_CESTA_PATRO_HINT,
  CONTRACT_PARTY_NP_HINT_CS,
  CONTRACT_PARTY_NP_PLACEHOLDER_CS,
  emptyDigitalniCestaPathSpec,
  normalizeDigitalniCestaSpec,
  resizeDigitalniCestaSpec,
  isDigitalniCestaPathSpecValid,
  isDigitalniCestaSpecValid,
  isDigitalniCestaPatroValid,
  sanitizeDigitalniCestaSpecForSave,
  formatDigitalniCestaPatro,
  parseDigitalniCestaPatro,
  normalizeContractPartyNpValue,
  type DigitalniCestaPathSpec,
  type DigitalniCestaPatroParts,
} from '@cubby/shared/src/constants/object-workflow/digitalni-cesta-spec.constants';

export {
  NAPOJENE_MT_COLORS,
  NAPOJENE_MT_COLOR_LABELS,
  NAPOJENE_MT_COLOR_HEX,
  napojeneMtColorSwatchStyle,
  isNapojeneMtColor,
  normalizeNapojeneMtBarvy,
  toggleNapojeneMtColor,
  isNapojeneMtBarvySelectionValid,
  isOdbocenoNapojeneMtBarvySatisfied,
  resolveOdbocenoNapojeneMtBarvy,
  formatNapojeneMtBarvyLabels,
  formatNapojeneMtBarvyLabelsOrUnspecified,
  readNapojeneMtBarvyFromMetadata,
  applyNapojeneMtBarvyForBudovaToMetadata,
  readNapojeneMtBarvyForBudova,
  readBranchParentBudovaIdsFromMetadata,
  filterNapojeneMtSlotsForRozvadec,
  isSpojkovisteZafukRozvadecSelectionValid,
  isSpojkovisteNapojeneMtBarvySelectionValid,
  applyNespecifikovanoDefaultsToSpojkovisteBarvyByBudova,
  NAPOJENE_MT_UNSPECIFIED,
  resolveSpojkovisteSlotNapojeneMtBarvy,
  ZAFUKNUTA_MT_BARVA_METADATA_KEY,
  normalizeZafuknutaMtBarva,
  readZafuknutaMtBarvaFromMetadata,
  applyZafuknutaMtBarvaToMetadataPatch,
  formatZafuknutaMtBarvaLabel,
  type NapojeneMtBarvySlot,
  type NapojeneMtSlot,
  type NapojeneMtZafukRozvadecSelection,
  type NapojeneMtColor,
} from '@cubby/shared/src/constants/napojene-mt-color.constants';

export {
  BUDOVA_MILESTONE_IDS,
  BUDOVA_MILESTONE_LABELS,
  BUDOVA_BRANCH_SNAP_OBJECT_TYPES,
  normalizeBudovaBranchSnapLineType,
  isBudovaBranchSnapLineType,
  type BudovaBranchSnapObjectType,
  getVisibleMilestoneIds,
  getRequiredMilestones,
  isBudovaNepripojenyPhase,
  shouldShowBudovaProgressMilestones,
  isBudovaWaitingForProgressMilestone,
  isMilestoneComplete,
  budovaMilestoneSkipsPhotoRequirement,
  isZrizeneVrSpecValid,
  isExplicitBudovaBuildingTypeReset,
  allMilestonesComplete,
  shouldAutoEnterSingleOpenBudovaMilestone,
  canAdvanceLinearPhase,
  budovaHasDigitalniCesta,
  budovaRequiresSvarPhase,
  resolveBudovaPripojenaPhaseColorOverride,
  canChangeBuildingType,
  parseBudovaMilestonesFromMetadata,
  isOdbocenoComplete,
  isOdbocenoMilestonePersisted,
  isOdbocenoWaivedByPripojenePlastyStatus,
  isOdbocenoSatisfiedForUi,
  resolveOdbocenoUiAction,
  resolveOdbocenoNavigateTarget,
  isOdbocenoVykopDone,
  isOdbocenoSpojDone,
  type OdbocenoNavigateTarget,
  resolveBranchVykopSegmentIds,
  mergeBudovaMilestoneFlagsFromBranchStatus,
  readBranchVykopSegmentIdsFromMetadata,
  buildBudovaBranchClearMetaPatch,
  type BranchVykopMapLookup,
  inferBudovaBuildingType,
  buildBudovaLinearNextPhaseAfterMilestones,
  buildBudovaAdminPripojeneOverridePhase,
  isBudovaMilestoneHostPhase,
  shouldFilterEditableNextPhasesForBudovaMilestones,
  filterPreviousPhasesForBudovaMilestones,
  resolveBudovaAutoPhaseAfterAllMilestones,
  resolveBudovaAggregateAdvanceTargetPhaseCode,
  readBudovaBuildingTypeFromMetadata,
  budovaBuildingTypeFromWorkflowBranchId,
  parseBudovaMilestonesMeta,
  parseBudovaBuildingTypeSelectionMeta,
  canRevertMilestone,
  canPickBudovaMilestone,
  buildBudovaMilestonePickerRows,
  canRevertBuildingType,
  buildBuildingTypeRevertMetaPatch,
  applyBuildingTypeRevertMetaPatch,
  type BudovaMilestonesMeta,
  type BudovaBuildingTypeSelectionMeta,
  type BudovaOdbocenoTriggerMeta,
  type BudovaMilestonePickerRow,
  type BudovaMilestoneId,
  type BudovaMilestonesFlags,
  type BudovaMilestonesState,
  type BudovaMilestoneEngineContext,
  type BudovaOdbocenoUiAction,
  type CanAdvanceLinearPhaseResult,
} from '@cubby/shared/src/constants/object-workflow/budova-milestone-engine';

export {
  BUDOVA_BRANCH_ORIGIN_KIND,
  odbocenoRequiresJunctionSpojkoviste,
  odbocenoRequiresNapojeneMtBarvy,
  readBudovaBranchOriginKind,
  readBudovaBranchRozvadecId,
  sanitizeOptionalObjectId,
  buildBudovaBranchRozvadecFkFields,
  type BudovaBranchOriginKind,
} from '@cubby/shared/src/constants/object-workflow/budova-branch-origin.constants';

export {
  ROZVADEC_MILESTONE_IDS,
  ROZVADEC_MILESTONE_LABELS,
  ROZVADEC_REQUIRED_MILESTONES,
  allRozvadecMilestonesComplete,
  buildRozvadecLinearNextPhaseAfterMilestones,
  canRevertRozvadecMilestone,
  hasPendingRozvadecWeldingMilestones,
  isRozvadecMilestoneComplete,
  isRozvadecMilestoneHostPhase,
  parseRozvadecMilestonesFromMetadata,
  parseRozvadecMilestonesMeta,
  resolveRozvadecAutoPhaseCodeForClient,
  shouldAutoEnterSingleOpenRozvadecMilestone,
  shouldShowRozvadecMilestonePickerPhaseBack,
  shouldFilterEditableNextPhasesForRozvadecMilestones,
  sumRozvadecWeldCounts,
  buildRozvadecWeldCountInfoRows,
  buildRozvadecOccupancyWeldInfoRows,
  type RozvadecMilestoneId,
  type RozvadecMilestonesFlags,
  type RozvadecMilestonesMeta,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-milestone-engine';

export {
  PARALLEL_MILESTONE_POPUP_MODE,
  resolveParallelMilestonePopupModeOnOpen,
  shouldOpenMilestonePickerOnObjectClick,
  type ParallelMilestonePopupModeOnOpen,
} from '@cubby/shared/src/constants/object-workflow/milestone-popup-mode';

export {
  buildBudovaMilestoneAttachmentPhaseKey,
  buildRozvadecMilestoneAttachmentPhaseKey,
  resolveAttachmentPhaseLabel,
  resolveAttachmentPhaseKey,
  inferAttachmentMilestoneIds,
  attachmentPhaseKeysForPhaseConfirm,
  rozvadecSvarenyPhotoAliasKeys,
  canonicalInfoOverviewPhaseKey,
  resolveInfoOverviewPhaseLabel,
  attachmentPhaseKeysForMilestoneConfirm,
  attachmentPhaseKeysForRozvadecMilestoneConfirm,
  photoMatchesAttachmentPhaseKeys,
  historyEntryAttachmentPhaseKey,
  type ResolveAttachmentPhaseKeyParams,
} from '@cubby/shared/src/constants/object-workflow/attachment-phase';

export {
  BUDOVA_PRIPOJENA_MAP_STRIPE_MILESTONES,
  BUDOVA_BABOVKA_MAP_STRIPE_MILESTONES,
  BUDOVA_PRIPOJENA_MAP_CASCADE_MILESTONES,
  BUDOVA_BABOVKA_MAP_CASCADE_MILESTONES,
  BUDOVA_MAP_MARKER_UNKNOWN_COLOR,
  BUDOVA_MAP_MARKER_ZAMITNUTA_COLOR,
  BUDOVA_MAP_MARKER_VIEWBOX_SIZE,
  BUDOVA_MAP_MARKER_SQUARE_INSET,
  BUDOVA_MAP_MARKER_STRIPE_GAP_PX,
  computeBudovaMapStripeRects,
  type BudovaMapMarkerStripeRect,
  resolveBudovaWorkflowNepripojenyPhaseCode,
  resolveBudovaMilestoneStripeColor,
  resolveBudovaMapMarkerNextPhaseColor,
  resolveBudovaMapStripeColors,
  shouldUseBudovaAggregateMapStripeColors,
  resolveBudovaMapMarkerModel,
  buildBudovaMapMarkerIconId,
  type BudovaMapMarkerModel,
  type BudovaMapMarkerStripe,
  type BudovaMapMarkerKind,
} from '@cubby/shared/src/constants/object-workflow/budova-milestone-map';

export {
  ROZVADEC_MAP_HALF_MILESTONES,
  ROZVADEC_MAP_MARKER_VIEWBOX_SIZE,
  ROZVADEC_MAP_MARKER_CIRCLE_INSET,
  ROZVADEC_MAP_MARKER_HALF_GAP_PX,
  computeRozvadecMapHalfRects,
  type RozvadecMapHalfRect,
  resolveRozvadecMilestoneHalfColor,
  shouldUseRozvadecHalvedMapMarker,
  resolveRozvadecMapMarkerModel,
  buildRozvadecMapMarkerIconId,
  type RozvadecMapMarkerModel,
  type RozvadecMapMarkerHalf,
  type RozvadecMapMarkerKind,
} from '@cubby/shared/src/constants/object-workflow/rozvadec-milestone-map';

// --- Drawings ---
export {
  OBJECT_TYPES_BY_DRAWING,
  DRAWING_IDS,
  DRAWING_IDS_LIST,
  DRAWING_LABELS,
  ROLE_TO_DRAWING,
  AKVIZICE_OBJECT_TYPES,
  isAcquisitionDrawing,
  isPasportizaceDrawing,
  getObjectTypesForDrawing,
  getDrawingLabel,
  getPickerDrawingsForUser,
  userCanViewAcquisitionDrawing,
  userCanViewDrawing,
  getPhaseColorForDrawing,
  canEditPhaseTo,
  isObjectTypeOnDrawing,
  EDITABLE_PHASES_BY_DRAWING,
  CREATABLE_POINT_TYPES_ORDER,
  ADMIN_ONLY_POINT_TYPES,
  getCreatablePointTypesOnDrawing,
  getCreatablePointTypesForUser,
  userCanCreatePointType,
  userCanCreateKoncovyBodZafuku,
  hasZafukCreateRole,
  isAdminOnlyPointType,
  canAdminDrawVykopOnDrawing,
  userCanEditObjectType,
  userCanCommitPhase,
  userHasRoleForDrawing,
  type DrawingId,
  type WorkflowDrawingId,
  type CreatablePointType,
  type AdminOnlyPointType,
} from '@cubby/shared/src/constants/drawing.constants';

export {
  SPEC_FIELD_REGISTRY,
  specFieldLabelCs,
  specFieldsForInfo,
  phaseConfirmFieldsForMilestone,
  phaseConfirmFieldsForTargetPhase,
  type SpecFieldId,
  type SpecFieldDef,
} from '@cubby/shared/src/constants/object-workflow/spec-field-registry';

export {
  MILESTONE_REGISTRY,
  BUDOVA_MILESTONE_REGISTRY,
  getMilestoneDef,
  requiredMilestoneIdsForBuildingType,
  BUDOVA_MILESTONES_PHASE_CONFIRM_ON_COMPLETE,
  BUDOVA_MILESTONES_AUTO_OPEN_ON_ENTER,
  BUDOVA_MILESTONES_AUTO_OPEN_ON_PICK,
  shouldAutoOpenBudovaMilestoneSpec,
  type MilestoneDef,
} from '@cubby/shared/src/constants/object-workflow/milestone-registry';

// --- Roles ---
export {
  ROLES,
  LEGACY_ROLE_URADY,
  LEGACY_ROLE_MANAGER,
  ASSIGNABLE_ROLES,
  ASSIGNABLE_ROLE_OPTIONS,
  ROLE_LABELS_CS,
  formatRoleLabelsCs,
  expandLegacyRoles,
  normalizeAssignableRoles,
  isAssignableRole,
  hasSuperAdminRole,
  hasFinanceAdminOrSuperAdminRole,
  hasFinanceOversightRole,
  hasFinanceRole,
  hasImportRole,
  hasImportOrSuperAdminRole,
  hasImportAdminOrSuperAdminRole,
  canDeleteMapObjects,
  canAccessFakturace,
  canAccessImportMenu,
  hasWarehouseAccessRole,
  hasWarehouseApiAccessRole,
  hasWarehouseEditRole,
  hasEquipmentAccessRole,
  hasEquipmentEditRole,
  hasWarehouseIssueRole,
  hasMaterialProcurementAccessRole,
  hasMaterialProcurementEditRole,
  isEligibleForWorkerWarehouse,
  formatWorkerWarehouseName,
  hasAdminOrSuperAdminRole,
  hasOrgAdminRole,
  sanitizeCompanyRoleGrants,
  filterRolesToCompanyGrants,
  canViewOtherUsersActivityAndWorkReport,
  canReadCompanyEarnings,
  canAccessWorkReportMenu,
  canAccessWorkReportAndRates,
  canAccessPdChangesNotes,
  canGenerateHrOnboardingContracts,
  canViewUserHrSalary,
  hasObjectContentMutatorRole,
  canCreateMapObjects,
  canGenerateAcquisitionContracts,
  canSignAcquisition,
  canCreatePolygonDocuments,
  canUseWorkShare,
  hasConstructionDiaryAccessRole,
  hasPolygonSpecificationEditorRole,
  hasEditorRole,
  hasViewerRole,
  hasAkviziceRole,
  hasDokumentaristaRole,
  hasPripravaStavebRole,
  hasBremenaRole,
  hasDatovkaRole,
  hasPersonalistkaRole,
  hasNavolavaniRole,
  hasSkladRole,
  hasFieldWorkRole,
  canAccessUserPersonalDriveFolder,
} from '@cubby/shared/src/constants/roles.constants';

export {
  APP_PERMISSION_SECTIONS,
  APP_PERMISSION_SECTION_IDS,
  COMPANY_DELEGATION_FORBIDDEN_SECTION_IDS,
  PERMISSION_ACTIONS,
  STAVBY_DELETE_FORBIDDEN_SECTION_IDS,
  buildCompanyPermissionCeiling,
  canManageCompany,
  canOpenCompanyList,
  canDeleteEquipmentSection,
  canDeleteMaterialsSection,
  canEditEquipmentSection,
  canEditMaterialsSection,
  canEditUserPermissionProfiles,
  canViewAuthorityHubTab,
  canViewEquipmentSection,
  canViewMaterialsSection,
  clampPermissionProfileToCeiling,
  emptyPermissionProfile,
  findPermissionCeilingViolations,
  hasPermission,
  isUnsetPermissionProfile,
  loadedPermissionProfile,
  normalizeCompanyDefaultPermissionProfile,
  normalizeUserPermissionProfile,
  expandLegacyPermissionProfile,
  PERMISSION_PRESET_ROLE_OPTIONS,
  permissionAllows,
  permissionProfileFromRoles,
  resolveRegistrationPermissionProfile,
  roleSeedsPermissionPreset,
  seedPermissionProfileFromLegacyAccess,
  visibleCompanyIds,
  type AppPermissionSectionId,
  type CompanyPermissionCeilingInput,
  type PermissionAction,
  type SectionPermission,
  type UserPermissionOrganizations,
  type UserPermissionProfile,
} from '@cubby/shared/src/constants/user-permission-profile';

export {
  NAVOLAVANI_DISPLAY_STATUSES,
  NAVOLAVANI_EDITABLE_STATUSES,
  NAVOLAVANI_STATUS_LABELS_CS,
  NAVOLAVANI_UNASSIGNED_POLYGON_SLUG,
  NAVOLAVANI_CALLBACK_REMINDER_OBJECT_TYPE,
  NAVOLAVANI_CALLBACK_REMINDER_PHASE_CODE,
  NAVOLAVANI_CALLBACK_REMINDER_LEAD_MS,
  canViewNavolavaniMenu,
  canEditNavolavani,
  canManageNavolavaniGoogle,
  isPrimaryNavolavaniGoogleCalendar,
  isNavolavaniPolygonId,
  isNavolavaniToCallStatus,
  isNavolavaniAppointmentStatus,
  isNavolavaniCallbackStatus,
  navolavaniStatusRequiresTechnician,
  navolavaniStatusRequiresCallbackAt,
  navolavaniStatusRequiresAgreedAt,
  isNavolavaniKNavolaniBuilding,
  countNavolavaniToCall,
  readNavolavaniKNavolaniCount,
  parseNavolavaniListScope,
  parseNavolavaniCalendarCreate,
  type NavolavaniDisplayStatus,
  type NavolavaniListScope,
  type NavolavaniBuildingsListCounts,
  type NavolavaniPolygonGroupCounts,
  type NavolavaniCalendarCreateInput,
  type NavolavaniCalendarCreateDecision,
} from '@cubby/shared/src/constants/navolavani.constants';

// --- HR nástupy (smlouvy při pozvánce) ---
export {
  HR_COMPANY_IDS,
  HR_CITY_IDS,
  HR_CITY_LABELS,
  HR_COMPANY_OPTIONS,
  HR_HEALTH_INSURERS,
  hrCitiesForCompany,
  hrDefaultCityForCompany,
  isHrCompanyId,
  type HrCompanyId,
  type HrCityId,
} from '@cubby/shared/src/constants/hr-onboarding.constants';

export {
  hrContractCityOptions,
  type HrContractCityOption,
} from '@cubby/shared/src/utils/hr-contract-city-options.util';

export {
  HR_MAILING_GROUPS_PROTELCO,
  HR_MAILING_GROUPS_FIBER_BETA,
  HR_MAILING_GROUP_LABELS,
  normalizeHrMailingGroups,
  hrMailingGroupsForCompany,
  type HrMailingGroupId,
} from '@cubby/shared/src/constants/hr-mailing-groups.constants';

export {
  HR_PEOPLE_DRIVE,
} from '@cubby/shared/src/constants/hr-people-drive.constants';

export {
  USER_DIRECTORY_STATUS_FILTER_OPTIONS_CS,
  USER_DIRECTORY_STATUS_LABELS_CS,
  userDirectoryStatusLabelCs,
  normalizeUserDirectoryStatus,
  normalizeUserDirectoryStatusFilter,
  USER_DIRECTORY_STATUSES,
  type UserDirectoryStatus,
  type UserDirectoryStatusFilter,
} from '@cubby/shared/src/constants/user-directory-status.constants';

export {
  CZECH_KRAJ_OPTIONS,
} from '@cubby/shared/src/constants/user-hr-kraj.constants';

export {
  USER_HR_CHECK_TYPES,
  USER_HR_CHECK_TYPE_LABELS_CS,
  USER_HR_CHECK_FREQUENCY_LABELS_CS,
  type UserHrCheckType,
  type UserHrCheckStatus,
  type UserHrCheckCalendarDto,
  type UserHrCheckCalendarRowDto,
  type UserHrCheckEventDto,
} from '@cubby/shared/src/constants/user-hr-check.constants';

export {
  type UserHrProfileFields,
  type UserHrProfileFieldsInput,
  type UserHrContractFields,
  type UserHrContractFieldsInput,
  normalizeUserHrProfile,
  normalizeUserHrContractFields,
  hrCompanyDisplayName,
  formatHrMailingGroupLabels,
} from '@cubby/shared/src/utils/user-hr-profile.util';

export { resolvePojistovnaLabel } from '@cubby/shared/src/utils/hr-onboarding.util';

export {
  WORKFLOW_NOTE_STATUSES,
  WORKFLOW_NOTE_STATUS_VALUES,
  WORKFLOW_NOTE_STATUS_LABELS_CS,
  isWorkflowNoteStatus,
  normalizeWorkflowNoteStatus,
  isUnresolvedWorkflowNoteStatus,
  type WorkflowNoteStatus,
} from '@cubby/shared/src/constants/workflow-note-status.constants';

export {
  PD_CHANGES_TABS,
  PD_CHANGES_TAB_VALUES,
  PD_CHANGES_TAB_LABELS_CS,
  PD_CHANGES_ITEM_KINDS,
  PD_CHANGES_ITEM_KIND_VALUES,
  PD_CHANGES_CUSTOM_FIELD_KEYS,
  isPdChangesTabId,
  isPdChangesItemKind,
  readPdChangesStatuses,
  readPdChangesItemStatus,
  readPdImportedNavaznost,
  ensurePdImportedNavaznostBaseline,
  type PdChangesTabId,
  type PdChangesItemKind,
  type PdImportedNavaznost,
  type PdChangesStatusesMap,
} from '@cubby/shared/src/constants/pd-changes.constants';

// --- Polygon business, geojson, work-report, excavation, acquisition, diary ---
export * from '@cubby/shared/src/constants/polygon-business.constants';
export * from '@cubby/shared/src/constants/project-drive-folder.constants';
export * from '@cubby/shared/src/constants/authority-message-drive.constants';
export * from '@cubby/shared/src/constants/polygon-documents.constants';
export * from '@cubby/shared/src/constants/authority.constants';
export {
  BREMENA_BRANCHES,
  BREMENA_BRANCH_LABELS_CS,
  BREMENA_CASE_WORK_STATUSES,
  BREMENA_CASE_WORK_STATUS_LABELS_CS,
  BREMENA_DOCUMENT_AXIS_STATUSES,
  BREMENA_DOCUMENT_AXIS_STATUS_LABELS_CS,
  BREMENA_KATASTR_AXIS_STATUSES,
  BREMENA_KATASTR_AXIS_STATUS_LABELS_CS,
  BREMENA_FINANCE_AXIS_STATUSES,
  BREMENA_FINANCE_AXIS_STATUS_LABELS_CS,
  BREMENA_READINESS_AXIS_STATUSES,
  BREMENA_READINESS_AXIS_STATUS_LABELS_CS,
  BREMENA_INSTRUMENT_TYPES,
  BREMENA_INSTRUMENT_TYPE_LABELS_CS,
  BREMENA_HUB_CONTRACT_TYPES,
  BREMENA_HUB_CONTRACT_COLUMN_LABELS_CS,
  BREMENA_PARTY_DOWNLOAD_FIXED_TYPES,
  BREMENA_E3_GENERATABLE_TYPES,
  BREMENA_SELF_SIGN_MAX_CZK,
  BREMENA_CHAROS_KOD_CODES,
  BREMENA_CHAROS_KOD_LABELS_CS,
  BREMENA_CHAROS_KOD_ORDER,
  BREMENA_CHAROS_KOD_104A_ELIGIBLE,
  BREMENA_CONSENT_KINDS,
  BREMENA_CONSENT_KIND_LABELS_CS,
  BREMENA_TEMPLATE_PLACEHOLDERS,
  BREMENA_TEMPLATE_CATALOG_STATUSES,
  BREMENA_TEMPLATE_CATALOG_STATUS_LABELS_CS,
  BREMENA_CONTRACT_STATUSES,
  BREMENA_CONTRACT_STATUS_LABELS_CS,
  isBremenaBranch,
  normalizeBremenaBranch,
  isBremenaCaseWorkStatus,
  normalizeBremenaCaseWorkStatus,
  isBremenaDocumentAxisStatus,
  normalizeBremenaDocumentAxisStatus,
  isBremenaKatastrAxisStatus,
  normalizeBremenaKatastrAxisStatus,
  isBremenaFinanceAxisStatus,
  normalizeBremenaFinanceAxisStatus,
  isBremenaReadinessAxisStatus,
  normalizeBremenaReadinessAxisStatus,
  isBremenaInstrumentType,
  normalizeBremenaInstrumentType,
  isBremenaCharOsKod,
  normalizeBremenaCharOsKod,
  bremenaSubjectKindFromCharOsKod,
  isBremenaCharOsKod104aEligible,
  isBremenaConsentKind,
  isBremenaE3GeneratableType,
  resolveBremenaConsentStubsAfterConfirm,
  resolveBremenaInstrumentsAfterConfirm,
  resolveBremenaPartyDownloadTypes,
  suggestBremenaInstrumentType,
  suggestBremenaLegalPath,
  isBremenaTemplatePlaceholder,
  isBremenaTemplateCatalogStatus,
  normalizeBremenaTemplateCatalogStatus,
  isBremenaContractStatus,
  normalizeBremenaContractStatus,
  normalizeBremenaConsentKind,
  BREMENA_LEGAL_PATH_RECOMMENDATION_CODES,
  BREMENA_LEGAL_PATH_RECOMMENDATION_LABELS_CS,
  type BremenaBranch,
  type BremenaCaseWorkStatus,
  type BremenaConsentKind,
  type BremenaDocumentAxisStatus,
  type BremenaKatastrAxisStatus,
  type BremenaFinanceAxisStatus,
  type BremenaReadinessAxisStatus,
  type BremenaInstrumentType,
  type BremenaHubContractType,
  type BremenaPartyDownloadType,
  type BremenaContractStatus,
  type BremenaCharOsKod,
  type BremenaCharOsKodKind,
  type BremenaPrivateContractPhase,
  type BremenaTemplatePlaceholder,
  type BremenaTemplateCatalogStatus,
} from '@cubby/shared/src/constants/bremena.constants';
export {
  advanceBremenaContractStatus,
  bremenaContractAllowsDownload,
  bremenaContractAllowsDownloadOrSend,
  bremenaContractAllowsPrepareSignature,
  bremenaContractAllowsSend,
  bremenaContractAllowsSendValidated,
  bremenaContractStatusLabel as sharedBremenaContractStatusLabel,
  bremenaContractStatusRank,
  deriveBremenaContractStatus,
  maxBremenaContractStatus,
} from '@cubby/shared/src/bremena/bremena-contract-status.util';

export {
  aggregateCoOwnerGroupCoverage,
  parseShareLabel,
  type BremenaCoOwnerCoverageMember,
  type BremenaCoOwnerGroupCoverage,
} from '@cubby/shared/src/bremena/bremena-co-owner-coverage.util';
export {
  ownerSharesSumToOne,
  parseShareFraction,
  sumOwnerShareFractions,
} from '@cubby/shared/src/bremena/bremena-party-share.util';

export {
  buildBremenaValidationReport,
  bremenaRequiresRevalidationAfterEdit,
  isBremenaDraftHtml,
  stampBremenaDraftWatermark,
  type BremenaValidationReport,
} from '@cubby/shared/src/bremena/bremena-production-validation.util';

export {
  applyBremenaStavbaSpecDefaults,
  bremenaStavbaFieldsMissingFromSpec,
  resolveBremenaOznaceniStavby,
  type BremenaStavbaFillKey,
  type BremenaStavbaFillResolved,
  type BremenaStavbaSpecFields,
} from '@cubby/shared/src/bremena/bremena-stavba-fill.util';

export {
  bremenaSigningRepresentativeLabelCs,
  computeBremenaNahradaTotalCzk,
  parseBremenaCzkAmount,
  resolveBremenaSigningRepresentative,
} from '@cubby/shared/src/bremena/bremena-nahrada.util';
export {
  BREMENA_DECISION_VALIDITY,
  applyBremenaDecisionImpact,
  bremenaNewOwnerContinuityMessage,
  canConfirmBremenaCirclePath,
  evaluateCharOs104aHint,
  proposeLegalPathForOwnerCircle,
  type BremenaDecisionValidity,
  type BremenaSection104aEligibility,
} from '@cubby/shared/src/bremena/bremena-legal-coverage.util';
export {
  BREMENA_NV_CUZK_URL,
  applyBremenaFillSources,
  bremenaCaseDraftFieldConflicts,
  bremenaContractEditorSections,
  bremenaFillManualKeys,
  bremenaFillSourcesFromPodklady,
  bremenaKatastrAssistEligibility,
  type BremenaCaseDraftSnapshot,
  type BremenaCaseFieldConflict,
  type BremenaContractEditorSections,
  type BremenaKatastrAssistEligibility,
  type BremenaKatastrAssistInput,
} from '@cubby/shared/src/bremena/bremena-fill-draft.util';
export {
  BREMENA_CSV_PROFILE_CUZK_VKLAD,
  BREMENA_CSV_PROFILE_PORTAL_STAVEBNIKA,
  BREMENA_CSV_PROFILES,
  getBremenaCsvProfile,
  getBremenaCsvProfileByTarget,
} from '@cubby/shared/src/bremena/csv/bremena-csv-profiles';
export {
  BREMENA_EVENT_POSSIBLE_SIGNED_DOCUMENT,
  BREMENA_EVENT_SIGNED_STATUS_WAIVER,
  BREMENA_POSSIBLE_SIGNED_DOCUMENT,
  BREMENA_SIGNED_WAIVER_REASON_MIN_LENGTH,
  isBremenaSignedWaiverReason,
} from '@cubby/shared/src/bremena/bremena-signed-review.util';
export {
  getBremenaTemplateVariant,
  listBremenaTemplateVariants,
} from '@cubby/shared/src/bremena/templates/template-catalog';
export type { BremenaTemplateCatalogEntry } from '@cubby/shared/src/bremena/templates/template-catalog.types';
export * from '@cubby/shared/src/constants/authority-domain.constants';
export * from '@cubby/shared/src/constants/authority-urady-categories.constants';
export * from '@cubby/shared/src/constants/authority-manual-obligation.constants';
export * from '@cubby/shared/src/constants/authority-territorial.constants';
export * from '@cubby/shared/src/constants/authority-overlay-map.constants';
export * from '@cubby/shared/src/constants/authority-overlay-map-display.constants';
export * from '@cubby/shared/src/constants/authority-letter-templates';
export * from '@cubby/shared/src/constants/network-crossing-operators.constants';
export type * from '@cubby/shared/src/constants/network-crossing.types';

export * from '@cubby/shared/src/constants/gantt-planning.constants';
export * from '@cubby/shared/src/constants/map-route-style.constants';
export * from '@cubby/shared/src/constants/dtm-ti-networks.constants';
export * from '@cubby/shared/src/constants/pasportizace.constants';
export * from '@cubby/shared/src/constants/nedodelky.constants';
export * from '@cubby/shared/src/constants/navaznost-map-layer.constants';
export {
  buildGanttPlanningQuantities,
  collectGanttDisplayEntriesFromSchedules,
  computeGanttActivitySchedules,
  computeGanttPlanningDurationDays,
  extractGanttPlanningQuantitiesFromPpdLines,
  ganttScheduleSortKey,
  maxGanttScheduleEndDate,
  parseGanttPlanningDate,
  resolveGanttPlanningCrewCounts,
  type GanttActivitySchedule,
  type GanttDisplayScheduleEntry,
  type GanttPlanningQuantities,
} from '@cubby/shared/src/utils/gantt-planning.util';
export {
  applyGanttPlanOverrides,
  ganttPlanOverridesFromMetadata,
  type GanttPlanOverrides,
} from '@cubby/shared/src/utils/gantt-plan-overrides.util';
export {
  deriveGanttActivityRealizations,
  resolveGanttActivityRealizationDates,
  type GanttActivityRealization,
} from '@cubby/shared/src/utils/gantt-activity-realization.util';
export { DRAWING_TYPE_MAPPING } from '@cubby/shared/src/constants/geojson.constants';
export { isGeojsonImportPolygonPickerEnabled } from '@cubby/shared/src/utils/geojson-import-target.util';
export { geoJsonContainsPoint } from '@cubby/shared/src/utils/geojson-contains-point.util';
export {
  isDxfFileName,
  isDgnFileName,
} from '@cubby/shared/src/utils/dxf-to-geojson.util';
export type {
  ImportSourceCrsCode,
  ResolvedImportSourceCrs,
} from '@cubby/shared/src/utils/import-source-crs.util';
export {
  IMPORT_SOURCE_CRS_CODES,
  detectImportSourceCrs,
  resolveImportSourceCrs,
  importSourceCrsIsWgs84,
} from '@cubby/shared/src/utils/import-source-crs.util';
export {
  NAVAZNOST_JSON_EXAMPLE,
  applyNavaznostManualAssignments,
  buildNavaznostLookups,
  navaznostMatchedByLabel,
  parseBudovaRozvadecNavaznostPayload,
  parseNavaznostManualAssignments,
  summarizeNavaznostMatchPreview,
  type NavaznostManualAssignment,
  type NavaznostManualAssignments,
} from '@cubby/shared/src/utils/budova-rozvadec-navaznost.util';
export {
  buildNavaznostBuildingLinkRows,
  navaznostBuildingLinkStatusLabel,
  summarizeNavaznostBuildingLinks,
  type NavaznostBuildingLinkList,
  type NavaznostBuildingLinkRow,
  type NavaznostBuildingLinkStatus,
} from '@cubby/shared/src/utils/budova-rozvadec-navaznost-list.util';
export {
  TRIM_VYKOP_ODOBOCKY_DEFAULT_MAX_HOPS,
  TRIM_VYKOP_ODOBOCKY_DEFAULT_TINY_M,
} from '@cubby/shared/src/utils/trim-vykop-odbocky.util';
export {
  DEFAULT_POLYGON_DIARY_STAVEBNIK,
  DEFAULT_POLYGON_DIARY_ZHOTOVITEL,
  PLACEHOLDER_POLYGON_BOUNDARY,
  buildPolygonImportMetadata,
} from '@cubby/shared/src/utils/polygon-import-metadata.util';
export {
  aggregatePolygonUnifiedHpById,
  resolvePolygonUnifiedHpCount,
  type PolygonUnifiedHpBuildingInput,
} from '@cubby/shared/src/utils/polygon-unified-hp.util';
export {
  WORK_QUEUE_TAB_IDS,
  WORK_QUEUE_TAB_LABELS,
  WORK_QUEUE_POLYGON_LIST_COLUMN_LABELS,
  WORK_QUEUE_DEFAULT_TAB,
  WORK_QUEUE_BUILDING_DP_TAB_IDS,
  WORK_QUEUE_ROZVADEC_TAB_IDS,
  WORK_QUEUE_MILESTONE_TAB_IDS,
  aggregateWorkQueueCounts,
  buildingHasUnifiedHp,
  emptyWorkQueueStats,
  formatWorkQueueAcquisitionCell,
  formatWorkQueueCount,
  hasAcquisitionStatus,
  isAcquisitionWorkComplete,
  isAcquisitionComplete,
  ACQUISITION_DRAWING_COLORS,
  resolveAcquisitionDrawingBuildingTone,
  getAcquisitionDrawingBuildingColor,
  isBabovkaReadyForZafuk,
  isBuildingInAcquisition,
  isBuildingReadyForZafuk,
  isBuildingWaitingForWeld,
  isRozvadecReadyForPostaveni,
  isRozvadecReadyForZafuk,
  isRozvadecWaitingForWeld,
  isRozvadecWorkQueueTab,
  isWorkQueueBuildingDpTab,
  isWorkQueueBuildingInScope,
  isWorkQueueBuildingMatch,
  isWorkQueueBuildingMilestoneComplete,
  isWorkQueueBuildingMissing,
  isWorkQueueRozvadecMatch,
  isWorkQueueRozvadecMilestoneComplete,
  isWorkQueueRozvadecMissing,
  parseWorkQueueTab,
  partitionWorkQueue,
  partitionWorkQueueByTab,
  partitionWorkQueueRozvadecByTab,
  resolveWorkQueueBuildingType,
  type AcquisitionDrawingBuildingTone,
  type WorkQueueBuildingInput,
  type WorkQueuePartition,
  type WorkQueuePolygonStats,
  type WorkQueueRozvadecInput,
  type WorkQueueTabId,
} from '@cubby/shared/src/utils/work-queue.util';

export {
  emptyNavolavaniEligibleCountsByKind,
  isNavolavaniEligibleRemaining,
  countNavolavaniEligibleByKind,
  countNavolavaniEligibleForKind,
  type NavolavaniEligibleCountsByKind,
} from '@cubby/shared/src/utils/navolavani-eligible.util';

export {
  ASSIGNED_WORK_DETAIL_COMPLETED_INFO,
  ASSIGNED_WORK_DETAIL_ON_TIME_INFO,
  ASSIGNED_WORK_KPI_INFO,
  buildAssignedWorkStatsTimeseries,
} from '@cubby/shared/src/utils/work-reservation-assigned-stats.util';
export type {
  AssignedWorkStatsBucket,
  AssignedWorkStatsTimeseriesPoint,
  AssignedWorkStatsTimeseriesResult,
} from '@cubby/shared/src/utils/work-reservation-assigned-stats.util';

export {
  WORK_RESERVATION_KINDS,
  NAVOLAVANI_WORK_KINDS,
  NAVOLAVANI_WORK_KIND_LABELS_CS,
  WORK_RESERVATION_KIND_LABELS_CS,
  WORK_RESERVATION_STATUSES,
  WORK_RESERVATION_STATUS_LABELS_CS,
  WORK_QUEUE_TAB_TO_RESERVATION_KIND,
  WORK_RESERVATION_EVENT_PREFIX,
  WORK_RESERVATION_KIND_OBJECT_TYPE,
  WORK_RESERVATION_MAP_OBJECT_TYPES,
  WORK_RESERVATION_MAP_HIGHLIGHT_COLORS,
  WORK_ASSIGNMENT_NOTIFICATION_OBJECT_TYPE,
  WORK_ASSIGNMENT_NOTIFICATION_PHASE_ASSIGNED,
  WORK_ASSIGNMENT_NOTIFICATION_PHASE_CANCELLED,
  isWorkReservationKind,
  isWorkReservationStatus,
  isWorkReservationMapObjectType,
  isNavolavaniWorkKind,
  parseWorkReservationKind,
  workReservationKindForWorkQueueTab,
  workQueueTabForReservationKind,
  workReservationObjectTypeForKind,
  hasWorkAssignRole,
  hasWorkReservationAssigneeRole,
  workReservationEventTitle,
  formatWorkReservationScheduleLabel,
  formatWorkReservationAssigneeNames,
  formatWorkReservationInfoRows,
  formatWorkReservationInfoText,
  formatWorkReservationInfoBlocks,
  pickWorkReservationForScheduleLabel,
  resolveWorkReservationOccupancy,
  resolveMapWorkReservationVisual,
  type WorkReservationKind,
  type NavolavaniWorkKind,
  type WorkReservationObjectType,
  type WorkReservationMapObjectType,
  type WorkReservationStatus,
  type WorkReservationDto,
  type WorkReservationSummary,
  type WorkReservationOccupancy,
  type WorkReservationInfoRow,
  type WorkReservationInfoBlock,
  type MapWorkReservationVisual,
} from '@cubby/shared/src/constants/work-reservation.constants';

export * from '@cubby/shared/src/constants/org-work-rates.util';
export * from '@cubby/shared/src/constants/work-report.constants';
export * from '@cubby/shared/src/constants/work-report-price-list.constants';
export * from '@cubby/shared/src/constants/excavation-material.constants';
export * from '@cubby/shared/src/constants/material-procurement.constants';
export * from '@cubby/shared/src/constants/material-warehouse-min-stock.constants';
export * from '@cubby/shared/src/constants/warehouse-category.constants';
export * from '@cubby/shared/src/constants/material-catalog.constants';
export * from '@cubby/shared/src/constants/acquisition-report.constants';
export {
  acquisitionPrefillFloorFlatCounts,
  isSpecHpUserEntered,
  prefillAcquisitionEntryDraft,
  prefillAcquisitionPartiesFromReport,
  prefillOwnerContacts,
  prefillParcelAddresses,
  prefillSpecCableType,
  readAcquisitionDisplayFields,
  readAcquisitionReportFields,
} from '@cubby/shared/src/utils/acquisition-form-prefill.util';
export * from '@cubby/shared/src/constants/acquisition-contract.constants';
export * from '@cubby/shared/src/constants/contract-document-parties';
export * from '@cubby/shared/src/constants/contract-document-party-form';
export * from '@cubby/shared/src/constants/ppd-report.constants';
export {
  parsePpdMatrix,
  parsePpdQuantityText,
  validatePpdParseResult,
  assertValidPpdMatrix,
  enrichPpdParseLineTotals,
} from '@cubby/shared/src/utils/ppd-parse.util';
export { mergeProtelcoCatalogSheets } from '@cubby/shared/src/utils/protelco-catalog-parse.util';
export {
  constructionDiaryEditWindowMinDate,
  diaryWeatherConditionLabel,
  DIARY_WEATHER_CONDITIONS,
  filterWorkSummaryEntriesForDiaryDay,
  getConstructionDiaryEditWindowDays,
  isConstructionDiaryEntryDateEditable,
  isTimestampOnPragueDate,
  normalizeDiaryEntryDateIso,
  pragueCalendarTodayIso,
  type DiaryWeatherConditionCode,
} from '@cubby/shared/src/constants/construction-diary.constants';

// --- Material ---
export {
  PRODUCT_CATEGORY_OBJECT_TYPE_ROOTS,
  PRODUCT_CATEGORY_ALL_ROOT_KEYS,
  PRODUCT_CATEGORY_ROOT_IDS,
  PRODUCT_CATEGORY_ROOT_LABELS,
  PRODUCT_CATEGORY_OBJECT_TYPE_ROOT_LABELS,
  OBJECT_TYPE_TO_PRODUCT_CATEGORY_ROOT,
  DEFAULT_PRODUCT_CATEGORY_SUBS,
  getProductCategoryRootKeyForObjectType,
  parseProductCategoryObjectTypeKey,
  normalizeProductCategoryPathSegments,
  isProductCategoryObjectTypeKey,
  isProductCategoryRootKey,
  type ProductCategoryObjectTypeKey,
  type ProductCategoryRootKey,
  type MaterialProductCategoryDto,
  type MaterialProductCategoryCreateInput,
  type MaterialProductCategoryUpdateInput,
} from '@cubby/shared/src/constants/material-product-category.constants';
export * from '@cubby/shared/src/constants/material-recommended-catalog.constants';
export * from '@cubby/shared/src/constants/material-stavajici-bundles.constants';

// --- Zařízení (equipment) ---
export {
  EQUIPMENT_SERVICE_EVENT_TYPES,
  EQUIPMENT_SERVICE_EVENT_TYPE_LABELS,
  EQUIPMENT_SERVICE_EVENT_STATUS_LABELS,
  EQUIPMENT_SERVICE_FREQUENCIES,
  EQUIPMENT_SERVICE_FREQUENCY_LABELS,
  EQUIPMENT_SERVICE_RECURRENCE_HORIZON_YEARS,
  EQUIPMENT_AVAILABILITY_LABELS,
  EQUIPMENT_HANDOVER_DIRECTION_LABELS,
  EQUIPMENT_HANDOVER_STATUS_LABELS,
  EQUIPMENT_HANDOVER_NOTIFICATION_OBJECT_TYPE,
  EQUIPMENT_HANDOVER_NOTIFICATION_PHASE_SIGN,
  EQUIPMENT_RETURN_ALREADY_PENDING_MESSAGE,
  equipmentRangesOverlap,
  findEquipmentScheduleConflicts,
  validateEquipmentReservationRange,
  equipmentOccupancyAt,
  expandEquipmentServiceOccurrenceStarts,
  addCalendarMonths,
  isEquipmentServiceFrequency,
  type EquipmentAvailability,
  type EquipmentCalendarDto,
  type EquipmentCalendarRowDto,
  type EquipmentDto,
  type EquipmentReservationDto,
  type EquipmentReservationStatus,
  type EquipmentServiceEventDto,
  type EquipmentServiceEventStatus,
  type EquipmentServiceEventType,
  type EquipmentServiceFrequency,
  type EquipmentSummaryDto,
  type EquipmentTimeRange,
  type EquipmentTransferDto,
  type EquipmentReceiptDocumentDto,
  type EquipmentReceiptLineDto,
  type EquipmentReceiptAttachmentDto,
  type EquipmentIssueDocumentDto,
  type EquipmentIssueLineDto,
  type EquipmentDetailDto,
  type EquipmentAttachmentDto,
  type EquipmentHandoverBatchDto,
  type EquipmentHandoverItemDto,
  type EquipmentHandoverDirection,
  type EquipmentHandoverStatus,
  type UserAssetCardSummaryDto,
} from '@cubby/shared/src/constants/equipment.constants';

export type { EquipmentNoteEntry } from '@cubby/shared/src/utils/equipment-notes.util';

export {
  EQUIPMENT_DRIVE,
  equipmentDriveFolderWebUrl,
} from '@cubby/shared/src/constants/equipment-drive.constants';

export {
  canModifyEquipmentNote,
  EQUIPMENT_NOTE_MAX_LENGTH,
} from '@cubby/shared/src/utils/equipment-notes.util';

export type { NedodelekNoteEntry } from '@cubby/shared/src/utils/nedodelky-notes.util';

export {
  canModifyNedodelekNote,
  NEDODELEK_NOTE_MAX_LENGTH,
} from '@cubby/shared/src/utils/nedodelky-notes.util';

export {
  formatNedodelekPhotoUploadedAt,
  nedodelekPhotoCaption,
  nedodelekPhotoUploaderFields,
  nedodelekPhotoUploaderName,
} from '@cubby/shared/src/utils/nedodelky-photo.util';

export { nedodelekOpenedByLabel } from '@cubby/shared/src/utils/nedodelek-opener.util';
export type {
  NedodelekPhotoUploaderFields,
  NedodelekPhotoUploaderInput,
} from '@cubby/shared/src/utils/nedodelky-photo.util';

export type {
  EquipmentAssignmentMode,
  EquipmentCategoryDto,
  EquipmentCategoryCreateInput,
  EquipmentCategoryUpdateInput,
  EquipmentCustomSpecFieldDef,
  EquipmentCustomSpecFieldType,
  EquipmentCustomSpecValue,
  EquipmentCustomSpecValues,
} from '@cubby/shared/src/constants/equipment-category.constants';

export {
  EQUIPMENT_ASSIGNMENT_MODES,
  EQUIPMENT_ASSIGNMENT_MODE_LABELS,
  EQUIPMENT_ASSIGNMENT_MODE_DEFAULT,
  EQUIPMENT_CUSTOM_SPEC_FIELD_TYPES,
  EQUIPMENT_CUSTOM_SPEC_FIELD_TYPE_LABELS,
  EQUIPMENT_VEHICLE_CATEGORY_NAME,
  EQUIPMENT_VEHICLE_DEFAULT_SPEC_FIELDS,
  allocateEquipmentCustomSpecKey,
  formatEquipmentCustomSpecValue,
  isEquipmentAssignmentMode,
  isEquipmentCustomSpecFieldType,
  normalizeEquipmentAssignmentMode,
  normalizeEquipmentCustomSpecFields,
  normalizeEquipmentCustomSpecValues,
  slugEquipmentCustomSpecKey,
  sortEquipmentCustomSpecFields,
} from '@cubby/shared/src/constants/equipment-category.constants';

export {
  getObjectTypeName,
  OBJECT_TYPE_KONCOVYBOD,
  isKoncovyBodZafukuType,
  isMapPointObjectType,
  MAP_POINT_OBJECT_TYPES,
  formatNamelessObjectLabel,
  formatObjectMapTooltip,
} from '@cubby/shared/src/constants/object-type-labels.constants';
export type { MapPointObjectType } from '@cubby/shared/src/constants/object-type-labels.constants';
export {
  VYKOP_VARIANT_IDS,
  VYKOP_VARIANT_IDS_LIST,
  VYKOP_VARIANT_LABELS,
  VYKOP_OBJECT_TYPE,
  VYKOP_TYPE_FILTER_OPTIONS,
  VYKOP_DOKOP_METADATA_KEYS,
  isVykopTypeFilterId,
  isDokopVykop,
  isBranchJunctionSpojkoviste,
  isHlavniTrasaVykop,
  getVykopVariantId,
  matchesVykopVariantFilter,
  resolveBranchParentBudovaId,
  type VykopVariantId,
  type VykopVariantLookup,
} from '@cubby/shared/src/constants/vykop-variant.constants';
export {
  VYKOP_SEGMENT_KINDS,
  VYKOP_SEGMENT_KIND_LABELS_CS,
  VYKOP_SEGMENT_KIND_FILTER_LABELS,
  VYKOP_SEGMENT_KIND_FILTER_OPTIONS,
  VYKOP_SEGMENT_KIND_FILTER_ORDER,
  VYKOP_MAP_SUBFILTER_IDS_LIST,
  VYKOP_MAP_SUBFILTER_OPTIONS,
  DEFAULT_VYKOP_SEGMENT_KIND,
  BRANCH_DRAW_VYKOP_SEGMENT_KIND,
  VYKOP_SEGMENT_KIND_METADATA_KEY,
  MAP_FILTER_TYP_TRASY_GROUP,
  MAP_FILTER_R100_REALIZED_LABEL,
  MAP_FILTER_PD_R100_UNDERLAY_ID,
  PD_R100_UNDERLAY_LAYER_ID,
  PD_R100_UNDERLAY_LAYER_LABEL,
  effectiveVykopSegmentKind,
  inferVykopSegmentKindFromLayerName,
  isPdProjectR100,
  isRealizedR100,
  isVykopSegmentKind,
  isVykopMapSubfilterId,
  matchesVykopMapSubFilters,
  parseVykopSegmentKind,
  type VykopSegmentKind,
} from '@cubby/shared/src/constants/vykop-segment-kind.constants';
export {
  formatAuthorityAutoDossReportCs,
  type AuthorityAutoDossReport,
  type AuthorityAutoDossBlocker,
} from '@cubby/shared/src/utils/authority-auto-doss.util';
export type {
  VykopSurfaceListRow,
  PathAdvanceBatchDetail,
  PathAdvanceBatchStreetAssignment,
} from '@cubby/shared/src/interfaces/path-advance-batch.interface';
export {
  parseExcavationSurfaceSnapshotsFromMetadata,
  formatExcavationSurfacesSummaryFromMetadata,
  buildVykopSurfaceListRowsFromGroup,
} from '@cubby/shared/src/utils/vykop-surface-rows.util';
export {
  digitsOnlyPhone,
  extractCzMobileNationalDigits,
  firstOwnerContactFromRows,
  hasOwnerContact,
  hasOwnerContactFromRows,
  isAcceptableCzMobilePhone,
} from '@cubby/shared/src/utils/owner-contact.util';
export {
  buildBudovaKnInfoSummary,
  formatBudovaOwnerNamesDisplay,
  formatContractOwnersSummary,
  formatContractParcelsSummary,
  formatOwnerContactDisplay,
  isOwnerCountSummaryLabel,
  pickBestContractForKnInfo,
  type BudovaKnInfoSummary,
  type BuildBudovaKnInfoSummaryInput,
  type ContractOwnerDisplayRow,
  type ContractParcelDisplayRow,
} from '@cubby/shared/src/utils/contract-parties-display.util';
export {
  collectDirectSapsFromNode,
  filterItemsByDirectCategory,
  indexMaterialLeavesBySap,
  pruneMaterialTreeByAllowedSap,
  type MaterialCatalogTreeNode,
} from '@cubby/shared/src/utils/material-tree-filter';
export {
  roundMaterialUsageQuantity,
  normalizeMaterialQuantityUnit,
  isWarehousePieceLengthUnit,
  isPieceLengthInputUnit,
  PIECE_LENGTH_INPUT_JEDNOTKA,
  shouldExpandWarehouseStockPieces,
  parseMaterialUnit,
  composeMaterialJednotka,
  canonicalizeMaterialJednotka,
  isLegacyPackUnitEncoding,
  purchaseToUsageQuantity,
  usageToPurchaseQuantity,
  purchaseToUsageUnitPrice,
  roundMaterialUnitPrice,
  toWarehouseStorageQuantity,
  usageQuantityToMeters,
  formatMaterialUnitLabel,
  formatMaterialQuantityLabel,
  resolveWarehouseMeasureUnit,
  preferredUsageJednotka,
  isPackagedMaterialUnit,
  isPieceContentLengthUnit,
  wholePurchasePacksError,
  assertWholePurchasePacks,
  normalizePurchaseQuantityInput,
  type MaterialUnitKind,
  type MaterialUnitInfo,
  type MaterialQuantityDisplayMode,
  type PurchasePackQuantityLine,
} from '@cubby/shared/src/utils/material-quantity.util';
export {
  requiresWholePieceQuantity,
  isWholePieceQuantity,
  normalizePieceLengthConsumeToUsageKs,
  warehouseMeterCutError,
  warehouseMeterPieceRequiredError,
  warehousePieceQuantityError,
  validateMaterialUsageStockCuts,
  warehouseWholePiecesError,
  type WarehouseQuantityOperation,
} from '@cubby/shared/src/utils/warehouse-quantity-rules.util';
export {
  expandWarehouseReceiptStockItems,
  warehouseReceiptCartLineTotal,
  warehouseReceiptPricedQuantity,
  wholeWarehouseMeterPieceCountError,
  aggregateWarehouseDocumentLinesForDisplay,
  aggregateWarehouseMaterialsSummary,
  formatWarehouseDocumentDisplayQty,
  formatWarehouseMaterialsSummaryQty,
  formatCompactWarehouseStockNoteBody,
  formatWarehouseReceiptAutoDescription,
  formatWarehouseIssueAutoDescription,
  formatWarehouseTransferAutoName,
  formatWarehouseTransferListTitle,
  formatWarehouseGoodsReceiptListTitle,
  formatWarehouseGoodsIssueListTitle,
  humanizeWarehouseAutoDocLabel,
  isWarehouseAutoDocDumpLabel,
  isWarehouseTransferAutoIdLabel,
  compactWarehouseAutoDescriptionForDisplay,
  type WarehouseReceiptCartLineInput,
  type WarehouseReceiptStockItem,
  type WarehouseDocumentLineForAggregate,
  type WarehouseDocumentDisplayLine,
  type WarehouseMaterialsSummaryLine,
  type WarehouseStockNoteLineInput,
} from '@cubby/shared/src/utils/warehouse-receipt-lines.util';
export {
  normalizeWarehousePieceLabel,
  WAREHOUSE_PIECE_LABEL_MAX_LENGTH,
} from '@cubby/shared/src/utils/warehouse-piece-label.util';
export {
  buildWarehouseStockExpandRows,
  canEditWarehouseStockBatch,
  groupWarehouseStockPiecesByBatch,
  normalizeWarehouseBatchNumber,
  warehouseStockBatchExpandKey,
  WAREHOUSE_BATCH_NUMBER_MAX_LENGTH,
} from '@cubby/shared/src/utils/warehouse-stock-expand.util';
export { isMaterialStockBelowMinimum } from '@cubby/shared/src/utils/material-min-stock.util';
export {
  UNASSIGNED_PRODUCT_LABEL_CS,
  catalogItemDisplayName,
  firstRealProductName,
  isSapPlaceholderProductName,
  resolveProductDisplayName,
} from '@cubby/shared/src/utils/product-display-name.util';
export {
  materialReplenishmentStockKey,
  purchaseQuantityToCoverUsageDeficit,
  buildMaterialReplenishmentProposals,
  formatMaterialReplenishmentDigestMessage,
  type MaterialReplenishmentMinRow,
  type MaterialReplenishmentStockRow,
  type MaterialReplenishmentProposalLine,
  type MaterialReplenishmentProposalsResult,
} from '@cubby/shared/src/utils/material-replenishment.util';
export {
  WAREHOUSE_SCAN_PREFIX,
  WAREHOUSE_SCAN_PREFIX_MATERIAL,
  WAREHOUSE_SCAN_PREFIX_POSITION,
  encodeWarehouseScanCode,
  encodeLegacyWarehouseScanCode,
  encodeMaterialScanCode,
  encodePositionScanCode,
  decodeWarehouseScanCode,
  isWarehouseScanCode,
  isMaterialScanCode,
  isPositionScanCode,
  asLegacyWarehouseScanPayload,
  formatPositionLabelDisplay,
  type WarehouseScanPayload,
  type WarehouseScanDecoded,
  type WarehouseScanKind,
  type WarehouseMaterialScanPayload,
  type WarehousePositionScanPayload,
  type WarehouseLegacyScanPayload,
} from '@cubby/shared/src/utils/warehouse-scan-code.util';
export {
  resolveWarehouseUiGroup,
  looksLikeWorkerWarehouseName,
  compareWarehousesByUiGroup,
  sortWarehousesByUiGroup,
  isPersonalWarehouse,
  WAREHOUSE_UI_GROUP_LABELS,
  WAREHOUSE_DELETE_DENIED_PERSONAL_CS,
  type WarehouseUiGroup,
  type WarehouseUiGroupInput,
} from '@cubby/shared/src/utils/warehouse-ui-group.util';
export {
  warehouseSpecLines,
  warehouseSpecHasFields,
  warehouseSpecCaption,
  warehouseSpecParty,
  warehouseSpecHasParty,
  warehousePdfParty,
  fillPartyFromWarehouseSpec,
  type WarehouseSpecFields,
  type WarehouseSpecLine,
  type WarehouseSpecParty,
} from '@cubby/shared/src/utils/warehouse-spec.util';
export {
  evaluateWarehouseIssueAccess,
  canIssueStockFromWarehouse,
  normalizeWarehouseIdList,
  accessiblePersonalWarehouseIds,
  filterWarehousesVisibleToActor,
  filterWarehousesForTransferPicker,
  filterWarehousesForListScope,
  canReadWarehouseStock,
  parseWarehouseListScope,
  WAREHOUSE_ISSUE_DENIED_BRANCH_CS,
  WAREHOUSE_ISSUE_DENIED_OTHER_WORKER_CS,
  WAREHOUSE_ISSUE_DENIED_NO_WORKER_LINK_CS,
  type WarehouseIssueActor,
  type WarehouseIssueAccessResult,
} from '@cubby/shared/src/utils/warehouse-issue-acl.util';
export {
  evaluateWarehouseReturnAccess,
  canReturnStockToBranch,
  WAREHOUSE_RETURN_DENIED_FROM_NOT_WORKER_CS,
  WAREHOUSE_RETURN_DENIED_TO_NOT_BRANCH_CS,
  type WarehouseReturnAccessResult,
} from '@cubby/shared/src/utils/warehouse-return-acl.util';
export {
  evaluateWarehouseTransferAccess,
  canTransferStockBetweenWarehouses,
  type WarehouseTransferAccessResult,
} from '@cubby/shared/src/utils/warehouse-transfer-acl.util';
export {
  resolveWarehouseTransferConfirmation,
  isPendingWarehouseTransferStatus,
  isWarehouseTransferConfirmNotification,
  warehouseTransferConfirmationActionLabel,
  warehouseTransferConfirmKindFromPhase,
  WAREHOUSE_TRANSFER_CONFIRMATION_ACTION_CS,
  WAREHOUSE_TRANSFER_CONFIRMATION_PENDING_CS,
  WAREHOUSE_TRANSFER_CONFIRMATION_PRIJEM,
  WAREHOUSE_TRANSFER_CONFIRMATION_VYDEJ,
  WAREHOUSE_TRANSFER_NOTIFICATION_OBJECT_TYPE,
  WAREHOUSE_TRANSFER_REJECT_ACTION_CS,
  type WarehouseTransferConfirmationKind,
} from '@cubby/shared/src/utils/warehouse-transfer-confirmation.util';
export {
  evaluateWarehouseReceiptAccess,
  canReceiveStockToWarehouse,
  WAREHOUSE_RECEIPT_DENIED_ROLE_CS,
  WAREHOUSE_RECEIPT_DENIED_NOT_BRANCH_CS,
  type WarehouseReceiptAccessResult,
} from '@cubby/shared/src/utils/warehouse-receipt-acl.util';
export {
  WAREHOUSE_MOVEMENT_CHANGE_TYPES,
  warehouseMovementLabelCs,
  isWarehouseReceiptMovement,
  receiptMovementUnitPrice,
  receiptMovementLineTotal,
  type WarehouseMovementChangeType,
  type WarehouseMovementDto,
  type WarehouseMovementLine,
} from '@cubby/shared/src/utils/warehouse-movement.util';
export {
  ADJACENT_POINT_COORD_DECIMALS,
  canonicalAdjacentPointHash,
  formatAdjacentPointHash,
  parseAdjacentPointHash,
} from '@cubby/shared/src/utils/adjacent-point-hash.util';
export {
  findPathInAdjacencyGraph,
  haversineDistanceMeters,
  resolveVertexPathToSegmentIds,
} from '@cubby/shared/src/utils/route-path-resolution.util';
export {
  sortDiariesByDateThenDistance,
  type DiaryDistanceSortable,
} from '@cubby/shared/src/utils/geo-distance.util';
export {
  formatDeepeningWorkReportActivityName,
  formatWorkReportDeepeningSpecText,
  isDeepeningWorkReportActivityName,
  WORK_REPORT_DEEPENING_CATALOG_ACTIVITY_NAME,
  workReportDeepeningCm,
} from '@cubby/shared/src/utils/work-report-deepening-display.util';
export {
  formatSpecialRemovalRowForDisplay,
  formatSpecialRemovalRowsForDisplay,
} from '@cubby/shared/src/utils/special-removal-display.util';
export {
  formatWorkReportLineItemDetail,
  isObnovaWorkReportActivityName,
  isPokladkaHdpeMultiductWorkReportActivityName,
  isZafukSekundarnichOkWorkReportRow,
  resolveWorkReportObnovaM2,
  resolveWorkReportPokladkaHdpeLengthM,
  resolveWorkReportRouteLengthM,
} from '@cubby/shared/src/utils/work-report-line-item-detail.util';
export {
  buildWorkReportEntryEditContext,
  buildWorkReportEntryUpdatePayload,
  computeWorkReportPriceFromQuantity,
  computeWorkReportQuantityFromValues,
  filterWorkReportEditableFieldsForPhase,
  getWorkReportPhaseAssignmentParamsLockReason,
  isWorkReportPhaseAssignmentActivity,
  resolveWorkReportEditableFields,
  resolveWorkReportEditableSpecFields,
  readWorkReportEditableSpecValues,
  shouldShowWorkReportUkonceniVRdOtherField,
  validateWorkReportEditableSpecValues,
  shouldLockWorkReportPhaseAssignmentParams,
  type BuildWorkReportEntryEditContextOptions,
  type WorkReportEditableField,
  type WorkReportEditableParamKey,
  type WorkReportEditableSpecField,
  type WorkReportEditableSpecKey,
  type WorkReportEditableSpecValues,
  type WorkReportEditableValues,
  type WorkReportEntryEditContext,
} from '@cubby/shared/src/utils/work-report-entry-edit.util';
export {
  parseWorkReportEntryCreatedAtInput,
  validateWorkReportEntryCreatedAt,
  workReportCreatedAtToDatetimeLocalInput,
} from '@cubby/shared/src/utils/work-report-date-filter.util';
export {
  selectedCollaboratorEmails,
} from '@cubby/shared/src/utils/work-share.utils';
export {
  canEditWorkReportEntryParams,
  mayEditOthersWorkReport,
  mayEditOtherWorkReportRow,
  canDeleteWorkReportEntry,
  isExcavationSurfacesEditablePhase,
  isExcavationSurfacesEditableOnMap,
  isVykopSheetEditablePhase,
  isObnovaSheetEditablePhase,
  isVykopSheetVisible,
  isObnovaSheetVisible,
  isVykopSheetEditableOnMap,
  isObnovaSheetEditableOnMap,
  needsExcavationData,
  needsObnovaData,
  isWorkReportEntryOwnedByViewer,
} from '@cubby/shared/src/utils/excavation-surfaces-phase.util';
export {
  correspondingVykopExcavationBillingPhase,
  hasVykopPricingModeEvidence,
  inferExcavationPricingModeFromSurfaces,
  inferExcavationPricingModeFromWorkReportLedger,
  isVolumePhase1WorkReportListItemCode,
  isVykopExcavationBillingPhase,
  isVykopRestorationBillingPhase,
  readExplicitExcavationPricingMode,
  resolveExcavationPricingModeFromEvidence,
  resolveExcavationPricingModeWithSafeguards,
} from '@cubby/shared/src/utils/excavation-pricing-mode.util';
export type {
  ExcavationPricingModeEvidence,
  ResolveExcavationPricingModeInput,
  ResolveExcavationPricingModeResult,
  WorkReportLedgerModeHint,
} from '@cubby/shared/src/utils/excavation-pricing-mode.util';
export {
  ROUTE_MIXED_EXCAVATION_PRICING_MESSAGE,
  adjacencyGuardedByExcavationPricing,
  establishedRoutePricingMode,
  excavationPricingBoundaryMessage,
  findPathRespectingExcavationPricing,
  noteRouteEdgePricing,
  readRouteSegmentPricingMode,
  routeEdgeKey,
  routeSegmentsMixExcavationPricing,
} from '@cubby/shared/src/utils/route-excavation-pricing-guard.util';
export type { RouteEdgePricing } from '@cubby/shared/src/utils/route-excavation-pricing-guard.util';
export {
  areVykopDimensionsLocked,
  isVykopDimensionEditAllowed,
  sanitizeExcavationDataPreservingLockedVykop,
  VYKOP_DIMENSION_LOCK_SNAPSHOT_KEYS,
} from '@cubby/shared/src/utils/excavation-vykop-dimension-lock.util';
export type {
  ExcavationDataLike,
  SanitizeLockedVykopResult,
  VykopDimensionLockInput,
} from '@cubby/shared/src/utils/excavation-vykop-dimension-lock.util';
export {
  isMaterialAndWorkShareRelevantPhase,
  isMaterialBlockedObjectType,
  isWorkShareRelevantPhase,
  isWorkflowStatusFinalPhase,
} from '@cubby/shared/src/utils/material-work-share-phase.util';
export {
  canShowWorkReportRowSelection,
  canShowWorkReportInvoiceAction,
  canShowWorkReportSettleAction,
  canShowWorkReportBulkUnpaidAction,
  canShowWorkReportAdminBulkUnpaidAction,
  canShowWorkReportAdminBulkActions,
  canShowWorkReportInvoiceHistory,
  canPayWorkReportInvoicePermission,
  canPayWorkReportInvoiceSummary,
  canRevertPaidWorkReportInvoicePermission,
  canRevertPaidWorkReportInvoiceSummary,
  canDeleteWorkReportInvoicePermission,
  type WorkReportBillingScope,
  type WorkReportBillingPermissionInput,
} from '@cubby/shared/src/utils/work-report-billing-permissions.util';
export {
  buildWorkSummaryBaseLabel,
  excavationHeadlineTemplate,
  formatWorkSummaryLocationHeadline,
  isDiaryExcavationObjectType,
} from '@cubby/shared/src/utils/diary-work-summary-headline.util';
export {
  filterDiaryNoteParts,
  isDiaryPhotoCountNoiseLine,
  scrubDiaryNotePhotoCountNoise,
} from '@cubby/shared/src/utils/diary-note-noise.util';
export {
  diaryProtocolNoteStateOnOpen,
  shouldReplaceDiaryProtocolNote,
} from '@cubby/shared/src/utils/diary-protocol-note.util';
export {
  SURFACE_HANDOVER_CREATE_ROLES,
  SURFACE_HANDOVER_ROLE_LABELS,
  buildPredaniPovrchuNote,
  canCreateSurfaceHandover,
  formatPredaniLegalDate,
  formatPredaniReceiverZastupce,
  formatPredaniZastupceLabel,
  formatUserDisplayName,
  resolveSurfaceHandoverRoleLabel,
  type PredaniPovrchuNoteInput,
} from '@cubby/shared/src/utils/predani-povrchu.util';
export {
  buildPredaniOdpovednostiNote,
  formatPredaniOdpovednostiPeriod,
  resolveStavbyvedouciLabel,
  resolveStavbyvedouciUserId,
  type PredaniOdpovednostiNoteInput,
} from '@cubby/shared/src/utils/predani-odpovednosti.util';
export {
  buildBlankContractBodyText,
  currentBlankContractDatePrague,
  currentBlankContractTimePrague,
  formatBlankContractLegalDate,
  formatBlankContractPartyLabel,
  formatBlankContractPartySummary,
  normalizeBlankContractIco,
  normalizeBlankContractTime,
  type BlankContractBodyInput,
  type BlankContractPartyInput,
  type BlankContractPartyKind,
} from '@cubby/shared/src/utils/blank-contract.util';
export {
  buildChainedRoutePolylineFromSegments,
  diaryWorkSummarySegmentPath,
} from '@cubby/shared/src/utils/diary-work-route-polyline.util';
export {
  formatRozvadecLabel,
  formatBudovaZafukRozvadecLabel,
  resolveBudovaZafukRozvadecObjectId,
  type RozvadecLabelInput,
  type BudovaZafukRozvadecRef,
} from '@cubby/shared/src/utils/rozvadec-label.util';
export {
  BUDOVA_PRIPOJENI_EMPTY_ROZVADEC_LABEL,
  budovaPripojeniRozvadecSortItem,
  compareBudovaPripojeniRozvadecThenAddress,
  compareRozvadecGroups,
  compareWithinRozvadecGroup,
  formatBudovaPripojeniRozvadecCell,
  resolveRozvadecGroupKey,
  sortBudovaPripojeniRowsByRozvadec,
  type BudovaPripojeniRozvadecSortItem,
} from '@cubby/shared/src/utils/budova-pripojeni-rozvadec-group.util';
export {
  classifyImportSource,
  preferredSheetNamesForSource,
  type ClassifiedImportSource,
  type ImportFileClassificationInput,
  type ImportFileClassificationResult,
} from '@cubby/shared/src/utils/file-classifier.util';
export { ImportSourceType } from '@cubby/shared/src/constants/building-import-canonical.constants';
export {
  DEFAULT_OBJECT_IMPORT_MODE,
  OBJECT_IMPORT_MODE_OPTIONS,
  parseObjectImportMode,
  suggestUnifiedImportAction,
  type ObjectImportMode,
} from '@cubby/shared/src/utils/object-import-mode.util';
export {
  decodeCsvBuffer,
  decodeCzechTextBuffer,
  looksLikeUtf8MisreadAsCp1250,
} from '@cubby/shared/src/utils/csv-encoding.util';
export {
  attachOwnerLookupMeters,
  joinOwnerLookupCsvs,
  joinOwnerLookupTables,
  parseCzechDecimal,
  parseSemicolonCsv,
  stringifyOwnerLookupMatrix,
  type OwnerLookupCsvJoinResult,
  type OwnerLookupCsvRow,
} from '@cubby/shared/src/utils/owner-lookup-csv.util';
export { formatR100LengthCs } from '@cubby/shared/src/utils/r100-parcel-length.util';
export {
  foldAuthorityContactText,
  isExceptionalCatalogSubject,
  matchAuthorityContactForSubject,
  pairedContactDisplay,
} from '@cubby/shared/src/utils/authority-contact-pairing.util';
export {
  AUTHORITY_CATALOG_APPLY_NEW_KEY,
  buildAuthorityCatalogApplyByPolygon,
  formatAuthorityCatalogApplyReportCs,
  parseAuthorityCatalogApplyByPolygon,
  shouldApplyAuthorityCatalog,
  shouldApplyAuthorityCatalogForPolygon,
  type AuthorityCatalogApplyByPolygon,
  type AuthorityCatalogApplyReport,
} from '@cubby/shared/src/utils/authority-catalog-apply.util';
export {
  cleanedMapSearchQueryForExternal,
  foldMapSearchDiacritics,
  isHouseNumberMapSearchToken,
  matchesMapSearchQuery,
  normalizeMapSearchQuery,
  stripMapSearchNoise,
  tokenizeMapSearchQuery,
  tokenizeMapSearchQueryPreservingDiacritics,
} from '@cubby/shared/src/utils/map-search-query.util';
export {
  FILTER_OPTION_SEARCH_MIN_COUNT,
  foldFilterSearchText,
  showsFilterOptionSearch,
} from '@cubby/shared/src/utils/filter-option-search.util';
export {
  matchNavolavaniBuildingToLocation,
  navolavaniCalendarEventMatchesPolygons,
  navolavaniCalendarEventMatchesSearch,
  navolavaniCalendarPersonLabel,
  pairNavolavaniCalendarToPerson,
  connectedNavolavaniTechnicians,
  type NavolavaniBuildingLocationMatchInput,
  type NavolavaniConnectedCalendarInput,
  type NavolavaniCalendarPersonPairInput,
  type NavolavaniCalendarSearchEvent,
  type NavolavaniRegisteredPerson,
} from '@cubby/shared/src/utils/navolavani-calendar-match.util';
export {
  formatMapSearchObjectSubtitle,
  formatMapSearchStavbaLabel,
} from '@cubby/shared/src/utils/map-search-object-label.util';
export {
  deselectFilteredStavby,
  filterStavbyPickerRows,
  resolveUserStavbyVisibility,
  selectAllFilteredStavby,
  STAVBY_ACCESS_DENIED_MESSAGE,
  toggleStavbyPickerSelection,
  userCanSeePolygon,
  type StavbyPickerRow,
  type UserStavbyVisibility,
} from '@cubby/shared/src/utils/user-stavby-visibility.util';

/** Object popup session draft — SSOT v `@cubby/shared` (mobile-shared Kotlin mirror). */
export {
  type ObjectPopupLocalDraft,
  type ObjectPopupSpecDraftSnapshot,
  type ObjectPopupMaterialDraftRow,
  type WorkflowObjectNoteDraft,
  type PopupMaterialDraftSource,
  sessionReadyForDraftPersist,
  shouldSkipMaterialFlushMutations,
  materialFlushDeleteIds,
  collectRemovedMaterialUsageIds,
  resolveOverlayIfMeaningful,
  resolvePendingNoteOverlay,
  resolvePendingWeldCountOverlay,
  resolveExcavationDataOverlay,
  resolveSpecOverlay,
  resolveHpCountTotalForDraftOverlay,
  specSnapshotHasAnyContent,
  resolveVrTechnicalReportOverlay,
  resolveWorkflowNotesOverlay,
  resolveMaterialsOverlay,
  resolveRouteOverlay,
  resolveRozvadecMilestoneOverlay,
  resolveBudovaMilestoneOverlay,
  hasMeaningfulRozvadecZafukDraftEntries,
  resolveRozvadecZafukOverlay,
  hasPersistableObjectPopupDraft,
  hasMeaningfulExcavationData,
  serializeWorkflowObjectNotesForBaseline,
  mergeWorkflowNotesDraftOverlay,
  serializeMaterialsForBaseline,
  buildMaterialsBaselineJsonFromDraftRows,
  selectedMaterialToDraftRow,
  draftRowToSelectedMaterial,
  resolvePopupSpecGates,
  popupSpecGatesShowSpec,
  popupSpecGatesZafukMode,
  type PopupSpecGates,
} from '@cubby/shared/src/object-popup';

/** Badge slug type for StatusBadgeComponent (maps Czech polygon status → design-system classes). */
export type ObjectStatusBadgeType = 'no-info' | 'in-progress' | 'complete';
